import { useState, useEffect, useRef, useCallback } from 'react';
import * as api from './api';

const css = `

  /* Screenshot blocking - Android only */
  @media (not (hover: hover)) {
    body:not(.ios) { -webkit-user-select: none; user-select: none; }
    body:not(.ios) * { -webkit-touch-callout: none; }
  }

  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap');
  :root {
    --navy:#06345B;--navy-dark:#031E3A;--navy-mid:#0A4A80;
    --blue:#0052CC;--blue-light:#1976D2;--blue-pale:#EBF3FD;
    --white:#FFFFFF;--bg:#F0F2F5;
    --gray-50:#F8FAFC;--gray-100:#EEF2F6;--gray-200:#DDE3EA;--gray-300:#B8C4CE;
    --gray-400:#8FA0B0;--gray-500:#637282;--gray-700:#3A4A5C;--gray-900:#1A2433;
    --green:#0D6E3F;--green-bg:#E8F5EE;--green-border:#A8D5BA;
    --red:#B91C1C;--red-bg:#FEF2F2;--red-border:#FCA5A5;
    --amber-bg:#FFFBEB;--amber-border:#FCD34D;--amber-text:#78350F;
    --glass-bg:rgba(255,255,255,0.75);--glass-border:rgba(255,255,255,0.65);
    --glass-shadow:0 8px 32px rgba(6,52,91,0.12),0 2px 8px rgba(0,0,0,0.06);
    --r-sm:8px;--r:14px;--r-lg:18px;--r-xl:26px;--nav:68px;
  }
  *{box-sizing:border-box;margin:0;padding:0;-webkit-tap-highlight-color:transparent}
  html,body{height:100%;overflow-x:hidden}
  body{font-family:'Inter',-apple-system,sans-serif;background:var(--bg);color:var(--gray-700);-webkit-font-smoothing:antialiased;max-width:480px;margin:0 auto}
  #root{min-height:100vh;display:flex;flex-direction:column}
  .app-bg{position:fixed;inset:0;z-index:-1;background:linear-gradient(150deg,#031E3A 0%,#06345B 40%,#0A4A80 70%,#1565C0 100%)}

  .status-bar{background:rgba(3,30,58,0.8);backdrop-filter:blur(20px);-webkit-backdrop-filter:blur(20px);padding:10px 18px;display:flex;align-items:center;gap:8px;position:sticky;top:0;z-index:200;border-bottom:1px solid rgba(255,255,255,.08)}
  .sdot{width:7px;height:7px;background:#22C55E;border-radius:50%;animation:pdot 3s infinite;flex-shrink:0;box-shadow:0 0 8px rgba(34,197,94,.7)}
  @keyframes pdot{0%,100%{opacity:1}50%{opacity:.3}}
  .stext{font-size:11px;font-weight:600;color:rgba(255,255,255,.5);letter-spacing:1px;text-transform:uppercase}
  .slogo{margin-left:auto;font-size:11px;font-weight:800;color:rgba(255,255,255,.45);letter-spacing:1.5px}

  .bottom-nav{position:fixed;bottom:0;left:50%;transform:translateX(-50%);width:100%;max-width:480px;height:var(--nav);background:rgba(255,255,255,0.9);backdrop-filter:blur(24px);-webkit-backdrop-filter:blur(24px);border-top:1px solid rgba(255,255,255,.6);display:flex;z-index:300;box-shadow:0 -8px 24px rgba(6,52,91,.08)}
  .nav-item{flex:1;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:5px;cursor:pointer;border:none;background:transparent;padding:10px 0 14px;transition:all .2s}
  .nav-item.active .nav-ic svg{stroke:var(--blue)}
  .nav-item.active .nav-lbl{color:var(--blue);font-weight:700}
  .nav-ic svg{stroke:var(--gray-300);transition:stroke .2s}
  .nav-lbl{font-size:10px;color:var(--gray-400);font-weight:500}
  .page{flex:1;overflow-y:auto;padding-bottom:calc(var(--nav) + 20px)}

  .gcard{background:var(--glass-bg);backdrop-filter:blur(20px);-webkit-backdrop-filter:blur(20px);border:1px solid var(--glass-border);border-radius:var(--r-xl);box-shadow:var(--glass-shadow);margin:0 16px 12px;overflow:hidden}
  .gcard-body{padding:18px}

  .field{display:flex;flex-direction:column;gap:5px;margin-bottom:14px}
  .field:last-child{margin-bottom:0}
  .lbl{font-size:11px;font-weight:700;color:var(--gray-500);text-transform:uppercase;letter-spacing:.7px}
  .req{color:var(--red)}
  .inp{border:1.5px solid rgba(255,255,255,0.6);border-radius:var(--r-sm);padding:12px 14px;font-size:15px;font-family:inherit;color:var(--gray-900);background:rgba(255,255,255,0.85);transition:all .2s;width:100%;-webkit-appearance:none;font-weight:500}
  .inp:focus{outline:none;border-color:var(--blue);background:rgba(255,255,255,.98);box-shadow:0 0 0 3px rgba(0,82,204,.1)}
  .inp::placeholder{color:var(--gray-300);font-weight:400}

  .dir-grid{display:flex;flex-direction:column;gap:8px;padding:0 16px 16px}
  .dir-btn{border:1.5px solid rgba(255,255,255,0.55);background:rgba(255,255,255,0.72);backdrop-filter:blur(16px);-webkit-backdrop-filter:blur(16px);border-radius:var(--r-lg);padding:14px 16px;cursor:pointer;display:flex;align-items:center;gap:14px;transition:all .2s;box-shadow:0 2px 8px rgba(6,52,91,.06)}
  .dir-btn:active{transform:scale(.99)}
  .dir-btn.sel{border-color:rgba(255,255,255,.9);background:rgba(255,255,255,0.95);box-shadow:0 4px 16px rgba(6,52,91,.1)}
  .dir-flag{font-size:30px;flex-shrink:0;line-height:1}
  .dir-name{font-size:15px;font-weight:700;color:var(--gray-900)}
  .dir-city{font-size:12px;color:var(--gray-500);margin-top:2px}

  .media-grid{display:grid;grid-template-columns:1fr 1fr;gap:10px}
  .mbtn{border:1.5px solid rgba(255,255,255,0.5);background:rgba(255,255,255,0.65);backdrop-filter:blur(12px);border-radius:var(--r-lg);padding:18px 12px;cursor:pointer;text-align:center;transition:all .2s;display:flex;flex-direction:column;align-items:center;gap:8px}
  .mbtn:active{transform:scale(.98)}
  .mbtn.done{border-color:var(--green);background:rgba(13,110,63,.1)}
  .mbtn-icon{width:40px;height:40px;border-radius:10px;display:flex;align-items:center;justify-content:center;background:rgba(6,52,91,.06);flex-shrink:0}
  .mbtn.done .mbtn-icon{background:rgba(13,110,63,.12)}
  .mbtn-icon svg{stroke:var(--gray-500)}
  .mbtn.done .mbtn-icon svg{stroke:var(--green)}
  .mbtn-lbl{font-size:12px;font-weight:600;color:var(--gray-700)}
  .mbtn.done .mbtn-lbl{color:var(--green)}
  .mbtn-sub{font-size:10px;color:var(--gray-400)}
  .mbtn-row{flex-direction:row;padding:14px 16px;gap:12px;text-align:left;margin-top:10px;width:100%}
  .mbtn-row .mbtn-lbl{font-size:13px}

  .queue-block{margin:0 16px 12px;background:rgba(255,251,235,0.88);backdrop-filter:blur(12px);border:1.5px solid rgba(252,211,77,.6);border-radius:var(--r-lg);padding:12px 16px;display:flex;align-items:center;gap:12px}
  .queue-icon{width:36px;height:36px;background:rgba(245,158,11,.12);border-radius:9px;display:flex;align-items:center;justify-content:center;flex-shrink:0}
  .queue-icon svg{stroke:#D97706}
  .queue-text{font-size:13px;color:var(--amber-text)}
  .queue-num{font-size:18px;font-weight:900;color:#B45309}

  .btn-primary{display:flex;align-items:center;justify-content:center;gap:8px;width:calc(100% - 32px);margin:0 16px 12px;background:linear-gradient(135deg,var(--navy) 0%,var(--navy-mid) 100%);color:white;border:none;border-radius:var(--r-lg);padding:16px;font-size:15px;font-weight:700;font-family:inherit;cursor:pointer;box-shadow:0 4px 20px rgba(6,52,91,.35);transition:all .2s;letter-spacing:.2px}
  .btn-primary:active{transform:scale(.98)}
  .btn-primary:disabled{background:rgba(255,255,255,.2);color:rgba(255,255,255,.35);box-shadow:none;cursor:not-allowed}
  .btn-primary.white-btn{background:white;color:var(--navy);box-shadow:0 4px 20px rgba(0,0,0,.15)}
  .btn-primary.white-btn:active{background:rgba(255,255,255,.9)}
  .btn-secondary{display:flex;align-items:center;justify-content:center;gap:6px;background:rgba(255,255,255,0.78);backdrop-filter:blur(12px);color:var(--navy);border:1.5px solid rgba(255,255,255,.65);border-radius:var(--r-sm);padding:12px 18px;font-size:14px;font-weight:600;font-family:inherit;cursor:pointer;transition:all .2s;box-shadow:0 2px 8px rgba(6,52,91,.08)}
  .btn-secondary:active{background:rgba(255,255,255,.95)}
  .btn-link{background:transparent;border:none;color:rgba(255,255,255,.5);font-size:13px;font-family:inherit;cursor:pointer;padding:8px;font-weight:500}

  .auth-screen{min-height:100vh;display:flex;flex-direction:column;align-items:center;justify-content:center;background:linear-gradient(150deg,#031E3A 0%,#06345B 50%,#0A4A80 100%);padding:24px;overflow:hidden}
  .auth-card{background:rgba(255,255,255,0.92);backdrop-filter:blur(32px);-webkit-backdrop-filter:blur(32px);border:1px solid rgba(255,255,255,.9);border-radius:var(--r-xl);padding:32px 24px;width:100%;max-width:380px;box-shadow:0 24px 64px rgba(0,0,0,.25)}
  .auth-logo-row{display:flex;align-items:center;gap:12px;margin-bottom:28px}
  .auth-logo-mark{width:48px;height:48px;background:linear-gradient(135deg,var(--navy) 0%,var(--navy-mid) 100%);border-radius:14px;display:flex;align-items:center;justify-content:center;box-shadow:0 4px 16px rgba(6,52,91,.25);flex-shrink:0}
  .auth-logo-mark svg{stroke:white}
  .auth-logo-text{display:flex;flex-direction:column}
  .auth-brand{font-size:18px;font-weight:900;color:var(--navy);letter-spacing:-.2px;line-height:1.1}
  .auth-brand-sub{font-size:11px;color:var(--gray-400);letter-spacing:.5px;margin-top:2px}
  .auth-title{font-size:22px;font-weight:800;color:var(--gray-900);margin-bottom:6px;letter-spacing:-.4px}
  .auth-hint{font-size:13px;color:var(--gray-500);margin-bottom:22px;line-height:1.55}
  .auth-err{background:var(--red-bg);border:1px solid var(--red-border);border-radius:var(--r-sm);padding:10px 14px;font-size:13px;color:var(--red);margin-bottom:14px;text-align:center;font-weight:500}
  .auth-link{text-align:center;margin-top:14px;font-size:13px;color:var(--gray-400)}
  .auth-link a{color:var(--blue);font-weight:600;cursor:pointer;text-decoration:none}
  .auth-btn{width:100%;background:var(--blue);color:white;border:none;border-radius:var(--r-lg);padding:15px;font-size:15px;font-weight:700;font-family:inherit;cursor:pointer;box-shadow:0 4px 16px rgba(0,82,204,.3);transition:all .2s;letter-spacing:.2px;margin-top:4px}
  .auth-btn:active{transform:scale(.99)}
  .auth-btn:disabled{background:var(--gray-200);color:var(--gray-400);box-shadow:none}
  .auth-btn-outline{width:100%;background:white;color:var(--navy);border:1.5px solid var(--gray-200);border-radius:var(--r-lg);padding:13px;font-size:14px;font-weight:600;font-family:inherit;cursor:pointer;transition:all .2s;margin-top:10px;display:flex;align-items:center;justify-content:center;gap:8px;box-shadow:0 2px 8px rgba(0,0,0,.04)}
  .auth-btn-outline:active{background:var(--gray-50)}

  .pin-row{display:flex;gap:10px;justify-content:center;margin:18px 0}
  .pin-cell{width:58px;height:64px;border:1.5px solid rgba(0,0,0,.1);border-radius:var(--r-lg);text-align:center;font-size:26px;font-weight:800;color:var(--navy);font-family:inherit;background:rgba(255,255,255,.7);transition:all .2s;outline:none;-webkit-appearance:none}
  .pin-cell:focus{border-color:var(--blue);background:white;box-shadow:0 0 0 3px rgba(0,82,204,.1)}

  .forbidden{min-height:100vh;display:flex;flex-direction:column;align-items:center;justify-content:center;background:linear-gradient(150deg,#031E3A,#06345B);padding:40px 32px;text-align:center;gap:16px}
  .forbidden-icon{width:80px;height:80px;background:rgba(255,255,255,.08);border-radius:24px;display:flex;align-items:center;justify-content:center}
  .forbidden-icon svg{stroke:rgba(255,255,255,.4)}
  .forbidden-title{font-size:22px;font-weight:800;color:white}
  .forbidden-text{font-size:14px;color:rgba(255,255,255,.45);max-width:280px;line-height:1.6}

  .steps{display:flex;align-items:center;padding:13px 16px;background:rgba(255,255,255,0.82);backdrop-filter:blur(16px);border-bottom:1px solid rgba(255,255,255,.5);gap:0;overflow-x:auto;scrollbar-width:none;position:sticky;top:41px;z-index:100}
  .steps::-webkit-scrollbar{display:none}
  .step{display:flex;align-items:center;gap:7px;flex-shrink:0}
  .step-num{width:22px;height:22px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:10px;font-weight:700;flex-shrink:0}
  .step-num.done{background:var(--green);color:white}
  .step-num.active{background:var(--navy);color:white}
  .step-num.idle{background:rgba(0,0,0,.08);color:var(--gray-400)}
  .step-lbl{font-size:11px;white-space:nowrap;font-weight:600}
  .step-lbl.active{color:var(--navy)}
  .step-lbl.done{color:var(--green)}
  .step-lbl.idle{color:var(--gray-300)}
  .step-sep{width:18px;height:1px;background:var(--gray-200);margin:0 8px;flex-shrink:0}
  .sec-head{padding:18px 16px 8px;font-size:11px;font-weight:700;color:rgba(255,255,255,.5);text-transform:uppercase;letter-spacing:1.2px}

  .pending-screen{min-height:100vh;display:flex;flex-direction:column;background:linear-gradient(150deg,#031E3A,#06345B,#0A4A80)}
  .pending-body{flex:1;display:flex;flex-direction:column;align-items:center;justify-content:center;padding:48px 28px;gap:24px;text-align:center}
  .spinner-ring{width:80px;height:80px;border:3px solid rgba(255,255,255,.1);border-top-color:rgba(255,255,255,.8);border-radius:50%;animation:spin 1s linear infinite}
  @keyframes spin{to{transform:rotate(360deg)}}
  .pending-title{font-size:22px;font-weight:800;color:white;letter-spacing:-.4px}
  .pending-sub{font-size:14px;color:rgba(255,255,255,.5);line-height:1.6;max-width:280px}
  .warn-card{border:1.5px solid rgba(252,165,165,.4);border-radius:var(--r-lg);background:rgba(185,28,28,.15);backdrop-filter:blur(12px);padding:16px 18px;max-width:340px;width:100%;text-align:left}
  .warn-card-head{font-size:13px;font-weight:700;color:#FCA5A5;display:flex;align-items:center;gap:8px;margin-bottom:6px}
  .warn-card-head svg{stroke:#FCA5A5;flex-shrink:0}
  .warn-card-text{font-size:13px;color:rgba(255,255,255,.65);line-height:1.5}
  .click-flash{position:fixed;inset:0;background:rgba(185,28,28,.2);z-index:999;display:flex;align-items:center;justify-content:center;animation:shake .4s ease;pointer-events:none}
  .click-flash-card{background:linear-gradient(135deg,#B91C1C,#7F1D1D);color:white;font-size:15px;font-weight:700;padding:14px 26px;border-radius:var(--r-lg);box-shadow:0 8px 32px rgba(185,28,28,.5);animation:scaleIn .2s ease;border:1px solid rgba(255,255,255,.15)}
  @keyframes shake{0%,100%{transform:translateX(0)}20%{transform:translateX(-8px)}40%{transform:translateX(8px)}60%{transform:translateX(-5px)}80%{transform:translateX(5px)}}
  @keyframes scaleIn{from{transform:scale(.85);opacity:0}to{transform:scale(1);opacity:1}}

  .success-screen{min-height:100vh;display:flex;flex-direction:column;background:linear-gradient(150deg,#031E3A,#064D2C,#0D6E3F)}
  .success-hero{padding:36px 20px 24px;text-align:center;display:flex;flex-direction:column;align-items:center;gap:14px}
  .success-mark{width:80px;height:80px;background:rgba(255,255,255,.12);border:2px solid rgba(255,255,255,.25);border-radius:50%;display:flex;align-items:center;justify-content:center;animation:scaleIn .5s cubic-bezier(.175,.885,.32,1.275);box-shadow:0 0 0 16px rgba(255,255,255,.05)}
  .success-mark svg{stroke:white}
  .success-title{font-size:22px;font-weight:900;color:white;letter-spacing:-.4px}
  .success-sub{font-size:13px;color:rgba(255,255,255,.55)}
  .appt-card{margin:0 16px 12px;background:rgba(255,255,255,.1);backdrop-filter:blur(20px);border:1px solid rgba(255,255,255,.2);border-radius:var(--r-xl);overflow:hidden;box-shadow:0 8px 24px rgba(0,0,0,.15)}
  .appt-row{display:flex;justify-content:space-between;align-items:center;padding:14px 18px;border-bottom:1px solid rgba(255,255,255,.1)}
  .appt-row:last-child{border-bottom:none}
  .appt-lbl{font-size:11px;color:rgba(255,255,255,.5);text-transform:uppercase;letter-spacing:.5px;font-weight:600}
  .appt-val{font-size:15px;font-weight:700;color:white}
  .appt-val.blue{color:#7DD3FC;font-size:20px;font-weight:900}
  .qr-card{margin:0 16px 12px;background:rgba(255,255,255,.95);border:1px solid rgba(255,255,255,.8);border-radius:var(--r-xl);padding:22px;display:flex;flex-direction:column;align-items:center;gap:14px;box-shadow:0 8px 24px rgba(0,0,0,.15)}
  .qr-label{font-size:11px;font-weight:700;color:var(--gray-500);text-transform:uppercase;letter-spacing:.8px}
  .qr-hint{font-size:12px;color:var(--gray-400);text-align:center;line-height:1.5}
  .tip-block{margin:0 16px 80px;background:rgba(255,255,255,.08);border:1px solid rgba(255,255,255,.15);border-radius:var(--r-lg);padding:14px 16px;font-size:13px;color:rgba(255,255,255,.6);line-height:1.6}
  .tip-block b{color:rgba(255,255,255,.85);font-weight:700}

  .error-screen{min-height:100vh;display:flex;flex-direction:column;background:linear-gradient(150deg,#031E3A,#7F1D1D,#B91C1C)}
  .error-screen.fin{background:linear-gradient(150deg,#031E3A,#06345B,#0A4A80)}
  .error-hero{padding:28px 20px 20px;text-align:center;display:flex;flex-direction:column;align-items:center;gap:12px}
  .error-mark{width:68px;height:68px;background:rgba(255,255,255,.12);border:2px solid rgba(255,255,255,.2);border-radius:50%;display:flex;align-items:center;justify-content:center;box-shadow:0 0 0 14px rgba(255,255,255,.05)}
  .error-mark svg{stroke:white}
  .error-title{font-size:19px;font-weight:800;color:white;line-height:1.3;max-width:280px;text-align:center}
  .error-sub{font-size:13px;color:rgba(255,255,255,.65);line-height:1.5;max-width:300px;margin-top:2px}
  .error-body{padding:0 16px 16px;display:flex;flex-direction:column;gap:10px}
  .err-block{background:rgba(255,255,255,.92);backdrop-filter:blur(16px);border-radius:var(--r-lg);padding:16px;box-shadow:0 4px 16px rgba(0,0,0,.1)}
  .err-block-title{font-size:11px;font-weight:700;color:var(--gray-400);text-transform:uppercase;letter-spacing:.8px;margin-bottom:10px;display:flex;align-items:center;gap:6px}
  .err-block-title svg{stroke:var(--gray-400);flex-shrink:0}
  .err-row{display:flex;justify-content:space-between;align-items:center;padding:8px 0;border-bottom:1px solid var(--gray-100)}
  .err-row:last-child{border-bottom:none}
  .err-row-lbl{font-size:13px;color:var(--gray-500)}
  .err-row-val{font-size:13px;font-weight:700;color:var(--gray-900)}
  .err-row-val.green{color:var(--green)}
  .err-row-val.timer{color:var(--navy);font-family:monospace;font-size:14px;font-weight:700}
  .err-amount-inline{font-weight:700;color:var(--navy);white-space:nowrap}
  .err-text{font-size:14px;color:var(--gray-700);line-height:1.75}
  .err-text p+p{margin-top:8px}
  .err-note{margin-top:12px;padding:12px 14px;background:rgba(6,52,91,.06);border-radius:10px;font-size:13px;color:var(--gray-700);line-height:1.55}
  .err-note b{color:var(--navy)}
  .err-important{background:rgba(185,28,28,.06);border-left:3px solid var(--red);padding:10px 14px;border-radius:0 8px 8px 0;font-size:13px;color:var(--gray-700);line-height:1.6;margin-top:10px}
  .upload-card{background:rgba(255,255,255,.15);backdrop-filter:blur(16px);border:1.5px solid rgba(255,255,255,.3);border-radius:var(--r-lg);padding:16px;display:flex;align-items:center;gap:14px;cursor:pointer;transition:all .2s;box-shadow:0 4px 12px rgba(0,0,0,.1)}
  .upload-card:active{background:rgba(255,255,255,.22)}
  .upload-icon{width:48px;height:48px;background:rgba(255,255,255,.15);border-radius:12px;display:flex;align-items:center;justify-content:center;flex-shrink:0}
  .upload-icon svg{stroke:white}
  .upload-title{font-size:14px;font-weight:700;color:white}
  .upload-sub{font-size:12px;color:rgba(255,255,255,.55);margin-top:2px}

  .profile-hero{background:linear-gradient(150deg,#031E3A 0%,#06345B 60%,#0A4A80 100%);padding:32px 20px 52px;text-align:center;display:flex;flex-direction:column;align-items:center;gap:10px;position:relative}
  .profile-hero::after{content:'';position:absolute;bottom:-1px;left:0;right:0;height:32px;background:var(--bg);border-radius:32px 32px 0 0}
  .profile-avatar{width:88px;height:88px;border-radius:50%;background:rgba(255,255,255,.1);overflow:hidden;display:flex;align-items:center;justify-content:center;border:2.5px solid rgba(255,255,255,.35);box-shadow:0 0 0 8px rgba(255,255,255,.06),0 8px 24px rgba(0,0,0,.2)}
  .profile-avatar img{width:100%;height:100%;object-fit:cover;display:block}
  .profile-avatar svg{stroke:rgba(255,255,255,.4)}
  .profile-name{font-size:22px;font-weight:900;color:white;letter-spacing:-.4px;margin-top:2px}
  .profile-email{font-size:13px;color:rgba(255,255,255,.45)}
  .profile-pill{display:inline-flex;align-items:center;gap:7px;background:rgba(34,197,94,.15);border:1px solid rgba(34,197,94,.3);border-radius:20px;padding:5px 14px;font-size:11px;font-weight:700;color:#4ADE80;letter-spacing:.4px}
  .profile-pill-dot{width:6px;height:6px;background:#22C55E;border-radius:50%;animation:pdot 2s infinite}
  .profile-content{background:var(--bg);padding-top:16px;min-height:300px}

  .info-card{margin:0 16px 12px;background:var(--glass-bg);backdrop-filter:blur(16px);border:1px solid var(--glass-border);border-radius:var(--r-lg);box-shadow:var(--glass-shadow);overflow:hidden}
  .info-row{display:flex;justify-content:space-between;align-items:center;padding:13px 16px;border-bottom:1px solid rgba(255,255,255,.6)}
  .info-row:last-child{border-bottom:none}
  .info-lbl{font-size:13px;color:var(--gray-500)}
  .info-val{font-size:13px;font-weight:700;color:var(--gray-900)}

  .slot-add-btn{margin:0 16px 8px;background:rgba(255,255,255,.72);backdrop-filter:blur(12px);border:1.5px dashed var(--gray-300);border-radius:var(--r-lg);padding:14px 16px;display:flex;align-items:center;gap:12px;cursor:pointer;transition:all .2s}
  .slot-add-icon{width:32px;height:32px;background:var(--blue);border-radius:9px;display:flex;align-items:center;justify-content:center;flex-shrink:0}
  .slot-add-icon svg{stroke:white}
  .slot-add-text{font-size:13px;color:var(--gray-500);font-weight:500}
  .slot-filled-card{margin:0 16px 8px;background:var(--glass-bg);backdrop-filter:blur(12px);border:1px solid var(--glass-border);border-radius:var(--r-lg);padding:14px 16px;display:flex;align-items:center;gap:12px;box-shadow:var(--glass-shadow)}
  .slot-num{width:30px;height:30px;background:var(--navy);border-radius:8px;display:flex;align-items:center;justify-content:center;font-size:13px;font-weight:700;color:white;flex-shrink:0}
  .slot-info{flex:1}
  .slot-name-text{font-size:14px;font-weight:600;color:var(--gray-900)}
  .slot-phone-text{font-size:12px;color:var(--gray-400);margin-top:1px}
  .slot-del{width:30px;height:30px;background:var(--red-bg);border-radius:8px;display:flex;align-items:center;justify-content:center;cursor:pointer;border:1px solid var(--red-border)}
  .slot-del svg{stroke:var(--red)}
  .slot-form{margin:0 16px 8px;background:rgba(255,255,255,.92);border-radius:var(--r-lg);padding:16px;box-shadow:var(--glass-shadow);border:1.5px solid var(--blue)}

  .mgr-footer{margin:12px 16px calc(var(--nav) + 16px);background:rgba(255,255,255,0.8);backdrop-filter:blur(24px);-webkit-backdrop-filter:blur(24px);border:1px solid rgba(255,255,255,.85);border-radius:var(--r-xl);box-shadow:0 8px 32px rgba(6,52,91,.12);overflow:hidden}
  .mgr-inner{padding:14px 16px;display:flex;align-items:center;gap:12px}
  .mgr-ava{width:46px;height:46px;border-radius:50%;background:var(--gray-100);overflow:hidden;flex-shrink:0;display:flex;align-items:center;justify-content:center;border:2px solid rgba(255,255,255,.8);box-shadow:0 2px 8px rgba(6,52,91,.1)}
  .mgr-ava img{width:100%;height:100%;object-fit:cover;display:block}
  .mgr-ava svg{stroke:var(--gray-400)}
  .mgr-details{flex:1;min-width:0}
  .mgr-label{font-size:10px;color:var(--gray-400);font-weight:700;text-transform:uppercase;letter-spacing:.7px}
  .mgr-name{font-size:14px;font-weight:700;color:var(--navy);white-space:nowrap;overflow:hidden;text-overflow:ellipsis;margin-top:1px}
  .mgr-badge{display:inline-flex;align-items:center;gap:4px;font-size:11px;color:var(--blue-light);font-weight:700;margin-top:2px}
  .mgr-badge-ic{width:15px;height:15px;background:var(--blue-light);border-radius:50%;display:inline-flex;align-items:center;justify-content:center}
  .mgr-badge-ic svg{stroke:white}
  .mgr-divider{width:1px;height:40px;background:var(--gray-100);flex-shrink:0}
  .mgr-stats{display:flex;flex-shrink:0}
  .mgr-stat{display:flex;flex-direction:column;align-items:center;padding:0 12px}
  .mgr-stat+.mgr-stat{border-left:1px solid var(--gray-100)}
  .mgr-stat-n{font-size:16px;font-weight:900}
  .mgr-stat-n.g{color:var(--green)}.mgr-stat-n.r{color:var(--red)}
  .mgr-stat-l{font-size:9px;color:var(--gray-400);text-transform:uppercase;letter-spacing:.4px;margin-top:1px}

  .camera-overlay{position:fixed;inset:0;background:black;z-index:1000;display:flex;flex-direction:column}
  .camera-video{width:100%;height:100%;object-fit:cover}
  .camera-ui{position:absolute;inset:0;display:flex;flex-direction:column;align-items:center;justify-content:center;pointer-events:none}
  .camera-darken{position:absolute;inset:0;background:rgba(0,0,0,.55)}
  .camera-title{position:absolute;top:60px;left:0;right:0;text-align:center;font-size:16px;font-weight:700;color:white;z-index:2}
  .camera-hint{position:absolute;bottom:155px;left:0;right:0;text-align:center;font-size:13px;color:rgba(255,255,255,.65);z-index:2;padding:0 32px;line-height:1.5}
  .cam-frame-passport{position:absolute;z-index:2;width:80%;max-width:310px;aspect-ratio:1.58/1;border-radius:12px;box-shadow:0 0 0 9999px rgba(0,0,0,.55)}
  .cam-frame-passport::before,.cam-frame-passport::after,.cam-frame-passport .c1,.cam-frame-passport .c2{content:'';position:absolute;width:28px;height:28px;border-color:#00E676;border-style:solid}
  .cam-frame-passport::before{top:-2px;left:-2px;border-width:3px 0 0 3px;border-radius:8px 0 0 0}
  .cam-frame-passport::after{top:-2px;right:-2px;border-width:3px 3px 0 0;border-radius:0 8px 0 0}
  .cam-frame-passport .c1{bottom:-2px;left:-2px;border-width:0 0 3px 3px;border-radius:0 0 0 8px}
  .cam-frame-passport .c2{bottom:-2px;right:-2px;border-width:0 3px 3px 0;border-radius:0 0 8px 0}
  .cam-scan-line{position:absolute;left:0;right:0;height:2px;background:linear-gradient(90deg,transparent,#00E676,transparent);animation:scan 2s ease-in-out infinite;z-index:3;box-shadow:0 0 8px #00E676}
  @keyframes scan{0%{top:5%}100%{top:95%}}
  .cam-frame-selfie{position:absolute;z-index:2;width:68%;max-width:260px;aspect-ratio:1/1;border-radius:50%;box-shadow:0 0 0 9999px rgba(0,0,0,.55),0 0 0 3px #00E676}
  .cam-step-badge{position:absolute;top:110px;left:50%;transform:translateX(-50%);background:rgba(0,0,0,.6);border:1px solid rgba(255,255,255,.2);border-radius:20px;padding:5px 14px;font-size:12px;color:rgba(255,255,255,.8);font-weight:600;z-index:3;white-space:nowrap}
  .camera-controls{position:absolute;bottom:50px;left:0;right:0;display:flex;align-items:center;justify-content:center;gap:40px;z-index:10;pointer-events:all}
  .cam-cancel-btn{width:50px;height:50px;background:rgba(255,255,255,.15);border-radius:50%;display:flex;align-items:center;justify-content:center;border:1.5px solid rgba(255,255,255,.3);cursor:pointer}
  .cam-cancel-btn svg{stroke:white}
  .cam-shutter{width:72px;height:72px;background:white;border-radius:50%;display:flex;align-items:center;justify-content:center;cursor:pointer;box-shadow:0 0 0 5px rgba(255,255,255,.25);transition:transform .1s}
  .cam-shutter:active{transform:scale(.94)}
  .cam-shutter-inner{width:56px;height:56px;background:white;border-radius:50%;border:3px solid #DDE3EA}

  .cam-success-overlay{position:absolute;inset:0;background:rgba(13,110,63,.7);display:flex;flex-direction:column;align-items:center;justify-content:center;gap:12px;z-index:20;animation:fadeInBg .3s ease}
  @keyframes fadeInBg{from{opacity:0}to{opacity:1}}
  .cam-success-check{width:80px;height:80px;background:white;border-radius:50%;display:flex;align-items:center;justify-content:center;animation:scaleIn .4s cubic-bezier(.175,.885,.32,1.275)}
  .cam-success-check svg{stroke:var(--green)}
  .cam-success-text{font-size:16px;font-weight:700;color:white}
  .cam-success-sub{font-size:13px;color:rgba(255,255,255,.75)}

  /* SUPPORT CHAT */
  .support-screen{position:fixed;top:0;left:50%;transform:translateX(-50%);width:100%;max-width:480px;bottom:var(--nav);display:flex;flex-direction:column;background:#F0F2F5;z-index:1;overflow:hidden}
  .support-header{background:linear-gradient(135deg,#031E3A 0%,#06345B 100%);padding:12px 16px 14px;display:flex;align-items:center;gap:12px;box-shadow:0 2px 12px rgba(0,0,0,.2);flex-shrink:0}
  .support-avatar{width:44px;height:44px;background:rgba(255,255,255,.15);border-radius:50%;display:flex;align-items:center;justify-content:center;border:2px solid rgba(255,255,255,.2);flex-shrink:0;font-size:20px}
  .support-title{font-size:16px;font-weight:700;color:white;letter-spacing:-.2px}
  .support-subtitle{font-size:12px;color:rgba(255,255,255,.55);margin-top:2px;display:flex;align-items:center;gap:5px}
  .support-online{width:7px;height:7px;background:#22C55E;border-radius:50%;animation:pdot 2s infinite;box-shadow:0 0 6px rgba(34,197,94,.6)}
  .support-messages{flex:1;overflow-y:auto;-webkit-overflow-scrolling:touch;padding:16px 12px 24px;display:flex;flex-direction:column;gap:8px;min-height:0}
  .msg-wrap-client{display:flex;flex-direction:column;align-items:flex-end}
  .msg-wrap-manager{display:flex;flex-direction:column;align-items:flex-start}
  .msg-sender{font-size:11px;color:var(--gray-400);margin-bottom:3px;padding:0 4px;font-weight:600}
  .msg-bubble{max-width:78%;padding:10px 14px;border-radius:18px;font-size:14px;line-height:1.5;word-break:break-word;position:relative}
  .msg-client{background:linear-gradient(135deg,#06345B,#0A4A80);color:white;border-bottom-right-radius:4px;box-shadow:0 2px 8px rgba(6,52,91,.25)}
  .msg-manager{background:white;color:var(--gray-900);border-bottom-left-radius:4px;box-shadow:0 2px 8px rgba(0,0,0,.08)}
  .msg-time{font-size:10px;margin-top:4px;padding:0 4px}
  .msg-time-client{color:rgba(255,255,255,.55);text-align:right}
  .msg-time-manager{color:var(--gray-400)}
  .msg-welcome{background:white;border-radius:18px;border-bottom-left-radius:4px;padding:14px 16px;font-size:14px;color:var(--gray-700);line-height:1.6;box-shadow:0 2px 8px rgba(0,0,0,.08);max-width:85%;align-self:flex-start}
  .msg-welcome-name{font-size:12px;color:var(--gray-400);margin-bottom:6px;font-weight:600}
  .support-input-bar{flex-shrink:0;background:rgba(255,255,255,.95);backdrop-filter:blur(20px);-webkit-backdrop-filter:blur(20px);border-top:1px solid var(--gray-100);padding:10px 12px;display:flex;gap:8px;align-items:flex-end;box-shadow:0 -4px 16px rgba(0,0,0,.06);safe-area-inset-bottom:env(safe-area-inset-bottom)}
  .support-input{flex:1;border:1.5px solid var(--gray-200);border-radius:22px;padding:10px 16px;font-size:14px;font-family:inherit;background:var(--gray-50);resize:none;outline:none;max-height:120px;line-height:1.5;transition:border-color .2s}
  .support-input:focus{border-color:var(--blue);background:white}
  .support-send-btn{width:42px;height:42px;background:linear-gradient(135deg,var(--navy),var(--navy-mid));border-radius:50%;border:none;cursor:pointer;display:flex;align-items:center;justify-content:center;flex-shrink:0;transition:all .2s;box-shadow:0 2px 8px rgba(6,52,91,.3)}
  .support-send-btn:active{transform:scale(.92)}
  .support-send-btn:disabled{background:var(--gray-200);box-shadow:none}
  .support-send-btn svg{stroke:white}

  @media(min-width:481px){body{border-left:1px solid var(--gray-100);border-right:1px solid var(--gray-100)}}
`;

/* QR CODE */
function QRCode({ value = 'VFS', size = 200 }) {
  const N = 25, cs = size / N;
  const seed = value.split('').reduce((a, c, i) => a + c.charCodeAt(0) * (i + 1), 0);
  const rng = (x, y) => { const h = Math.abs(Math.sin(x * 127.1 + y * 311.7 + seed) * 43758.5453); return h - Math.floor(h); };
  const fp = (r, c, or, oc) => { const lr = r-or, lc = c-oc; if(lr<0||lr>6||lc<0||lc>6)return null; if(lr===0||lr===6||lc===0||lc===6)return true; if(lr>=2&&lr<=4&&lc>=2&&lc<=4)return true; return false; };
  const getCell = (r, c) => {
    if(r<7&&c<7)return fp(r,c,0,0); if(r<7&&c>=N-7)return fp(r,c,0,N-7); if(r>=N-7&&c<7)return fp(r,c,N-7,0);
    if(r===6&&c>=8&&c<=N-9)return c%2===0; if(c===6&&r>=8&&r<=N-9)return r%2===0;
    if(r>=N-9&&r<=N-5&&c>=N-9&&c<=N-5){const ar=r-(N-7),ac=c-(N-7);if(ar===0||ar===4||ac===0||ac===4)return true;if(ar===2&&ac===2)return true;return false;}
    if(r===8&&c<9)return[true,false,true,true,false,true,true,true,true][c];
    return rng(r,c)>0.48;
  };
  const cells = Array.from({length:N},(_,r)=>Array.from({length:N},(_,c)=>getCell(r,c)));
  return (<svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{display:'block',borderRadius:8}}><rect width={size} height={size} fill="white" rx="8"/>{cells.map((row,r)=>row.map((cell,c)=>cell?<rect key={`${r}-${c}`} x={c*cs+.5} y={r*cs+.5} width={cs-.5} height={cs-.5} fill="#111" rx={cs>6?1.5:.5}/>:null))}</svg>);
}

/* ICONS */
const Ic = {
  Logo: () => <svg viewBox="0 0 24 24" width="24" height="24" fill="none" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2L2 7l10 5 10-5-10-5M2 17l10 5 10-5M2 12l10 5 10-5"/></svg>,
  Booking: ({a}) => <svg viewBox="0 0 24 24" width="22" height="22" fill="none" strokeWidth={a?2.2:1.8} strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4M8 2v4M3 10h18"/><path d="M8 14h.01M12 14h.01M16 14h.01M8 18h.01M12 18h.01"/></svg>,
  Profile: ({a}) => <svg viewBox="0 0 24 24" width="22" height="22" fill="none" strokeWidth={a?2.2:1.8} strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/></svg>,
  Check: ({s}) => <svg viewBox="0 0 24 24" width={s||36} height={s||36} fill="none" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 13l4 4L19 7"/></svg>,
  X: ({s}) => <svg viewBox="0 0 24 24" width={s||32} height={s||32} fill="none" strokeWidth="2.5" strokeLinecap="round"><path d="M18 6L6 18M6 6l12 12"/></svg>,
  Passport: () => <svg viewBox="0 0 24 24" width="20" height="20" fill="none" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="2" width="18" height="20" rx="2"/><circle cx="12" cy="10" r="3"/><path d="M6 18c0-2.2 2.7-4 6-4s6 1.8 6 4"/></svg>,
  Camera: () => <svg viewBox="0 0 24 24" width="20" height="20" fill="none" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z"/><circle cx="12" cy="13" r="4"/></svg>,
  Clip: () => <svg viewBox="0 0 24 24" width="20" height="20" fill="none" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48"/></svg>,
  Upload: () => <svg viewBox="0 0 24 24" width="24" height="24" fill="none" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>,
  Clock: () => <svg viewBox="0 0 24 24" width="18" height="18" fill="none" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>,
  Warn: () => <svg viewBox="0 0 24 24" width="16" height="16" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>,
  Shield: () => <svg viewBox="0 0 24 24" width="10" height="10" fill="none" strokeWidth="2.5" strokeLinecap="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>,
  Plus: () => <svg viewBox="0 0 24 24" width="14" height="14" fill="none" strokeWidth="2.5" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>,
  User: ({s}) => <svg viewBox="0 0 24 24" width={s||36} height={s||36} fill="none" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/></svg>,
  Arrow: () => <svg viewBox="0 0 24 24" width="18" height="18" fill="none" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>,
  Ban: () => <svg viewBox="0 0 24 24" width="40" height="40" fill="none" strokeWidth="1.5" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><line x1="4.93" y1="4.93" x2="19.07" y2="19.07"/></svg>,
  Back: () => <svg viewBox="0 0 24 24" width="16" height="16" fill="none" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>,
  Download: () => <svg viewBox="0 0 24 24" width="18" height="18" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>,
  Trash: () => <svg viewBox="0 0 24 24" width="14" height="14" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/><path d="M10 11v6M14 11v6M9 6V4h6v2"/></svg>,
  Send: () => <svg viewBox="0 0 24 24" width="18" height="18" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>,
  Support: ({a}) => <svg viewBox="0 0 24 24" width="22" height="22" fill="none" strokeWidth={a?2.2:1.8} strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg>,
  Ok: () => <svg viewBox="0 0 24 24" width="9" height="9" fill="none" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M5 13l4 4L19 7"/></svg>,
};

/* CAMERA */
function CameraCapture({ mode, stepLabel, onCapture, onClose }) {
  const videoRef = useRef(); const streamRef = useRef();
  const [flash, setFlash] = useState(false); // brief green flash, not blocking

  useEffect(() => {
    navigator.mediaDevices.getUserMedia({ video: { facingMode: mode === 'selfie' ? 'user' : 'environment', width:{ideal:1280}, height:{ideal:720} } })
      .then(stream => { streamRef.current = stream; if(videoRef.current){videoRef.current.srcObject=stream;videoRef.current.play();} })
      .catch(() => { alert('Нет доступа к камере'); onClose(); });
    return () => streamRef.current?.getTracks().forEach(t => t.stop());
  }, []);

  const shoot = () => {
    const v = videoRef.current; const canvas = document.createElement('canvas');
    canvas.width = v.videoWidth; canvas.height = v.videoHeight;
    canvas.getContext('2d').drawImage(v, 0, 0);
    // Brief flash feedback
    setFlash(true); setTimeout(() => setFlash(false), 300);
    canvas.toBlob(blob => {
      const f = new File([blob], `${mode}_${Date.now()}.jpg`, { type: 'image/jpeg' });
      onCapture(f); // immediate — no delay blocking screen
    }, 'image/jpeg', 0.92);
  };

  const isPassport = mode !== 'selfie';
  return (
    <div className="camera-overlay">
      <video ref={videoRef} className="camera-video" playsInline muted/>
      {/* Brief white flash on capture */}
      {flash && <div style={{position:'absolute',inset:0,background:'rgba(255,255,255,.4)',zIndex:15,pointerEvents:'none'}}/>}
      <div className="camera-ui">
        <div className="camera-darken"/>
        <div className="camera-title">{isPassport ? 'Наведите паспорт на рамку' : 'Поместите лицо в круг'}</div>
        {stepLabel && <div className="cam-step-badge">{stepLabel}</div>}
        {isPassport ? <div className="cam-frame-passport"><span className="c1"/><span className="c2"/><div className="cam-scan-line"/></div> : <div className="cam-frame-selfie"/>}
        <div className="camera-hint">{isPassport ? 'Все данные должны быть чётко видны' : 'Смотрите прямо в камеру'}</div>
      </div>
      <div className="camera-controls">
        <div className="cam-cancel-btn" onClick={() => { streamRef.current?.getTracks().forEach(t=>t.stop()); onClose(); }}><Ic.X s={20}/></div>
        <div className="cam-shutter" onClick={shoot}><div className="cam-shutter-inner"/></div>
        <div style={{width:50}}/>
      </div>
    </div>
  );
}

/* Passport capture — 2 steps with progress indicator */
function PassportCapture({ onDone, onClose }) {
  const [step, setStep] = useState(0);
  const [frontFile, setFrontFile] = useState(null);

  return (
    <div style={{position:'fixed',inset:0,zIndex:1000}}>
      {/* Progress indicator at top */}
      <div style={{position:'absolute',top:0,left:0,right:0,zIndex:30,background:'rgba(0,0,0,.7)',padding:'12px 20px',display:'flex',alignItems:'center',gap:10}}>
        <div style={{flex:1,height:4,background:'rgba(255,255,255,.2)',borderRadius:4,overflow:'hidden'}}>
          <div style={{height:'100%',width:step===0?'50%':'100%',background:'#00E676',borderRadius:4,transition:'width .3s'}}/>
        </div>
        <span style={{fontSize:12,color:'rgba(255,255,255,.7)',fontWeight:600,whiteSpace:'nowrap'}}>{step+1} / 2</span>
      </div>
      {step === 0
        ? <CameraCapture mode="passport" stepLabel="Лицевая сторона" onCapture={f => { setFrontFile(f); setStep(1); }} onClose={onClose}/>
        : <CameraCapture mode="passport" stepLabel="Обратная сторона" onCapture={f => onDone(frontFile, f)} onClose={onClose}/>
      }
    </div>
  );
}

function StatusBar() {
  return <div className="status-bar"><div className="sdot"/><span className="stext">Система регистрации ВЦ</span><span className="slogo">VFS GLOBAL</span></div>;
}

function BottomNav({active, onChange}) {
  return (
    <div className="bottom-nav">
      <button className={`nav-item ${active==='booking'?'active':''}`} onClick={()=>onChange('booking')}><div className="nav-ic"><Ic.Booking a={active==='booking'}/></div><span className="nav-lbl">Запись</span></button>
      <button className={`nav-item ${active==='support'?'active':''}`} onClick={()=>onChange('support')}><div className="nav-ic"><Ic.Support a={active==='support'}/></div><span className="nav-lbl">Поддержка</span></button>
      <button className={`nav-item ${active==='profile'?'active':''}`} onClick={()=>onChange('profile')}><div className="nav-ic"><Ic.Profile a={active==='profile'}/></div><span className="nav-lbl">Профиль</span></button>
    </div>
  );
}

function MgrFooter({manager}) {
  if (!manager) return null;
  return (
    <div className="mgr-footer"><div className="mgr-inner">
      <div className="mgr-ava">{manager.photo_url ? <img src={manager.photo_url} alt="" onError={e=>e.target.style.display='none'}/> : <Ic.User s={22}/>}</div>
      <div className="mgr-details">
        <div className="mgr-label">Учётная запись создана</div>
        <div className="mgr-name">{manager.fio||'Менеджер'}</div>
        <div className="mgr-badge"><div className="mgr-badge-ic"><Ic.Ok/></div> Проверенный партнёр VFS</div>
      </div>
      <div className="mgr-divider"/>
      <div className="mgr-stats">
        <div className="mgr-stat"><span className="mgr-stat-n g">{manager.success_visas}</span><span className="mgr-stat-l">успешно</span></div>
        <div className="mgr-stat"><span className="mgr-stat-n r">{manager.failed_visas}</span><span className="mgr-stat-l">отказов</span></div>
      </div>
    </div></div>
  );
}

function PinInput({onComplete, onRef}) {
  const [pins, setPins] = useState(['','','','']); const refs = [useRef(),useRef(),useRef(),useRef()];
  if (onRef) onRef({ clear: () => { setPins(['','','','']); setTimeout(()=>refs[0].current?.focus(),50); } });
  const hc = (i, val) => { if(!/^\d?$/.test(val))return; const n=[...pins];n[i]=val;setPins(n); if(val&&i<3)refs[i+1].current.focus(); if(i===3&&val)onComplete([...pins.slice(0,3),val].join('')); };
  return <div className="pin-row">{pins.map((p,i)=><input key={i} ref={refs[i]} className="pin-cell" type="password" inputMode="numeric" maxLength={1} value={p} onChange={e=>hc(i,e.target.value)} onKeyDown={e=>e.key==='Backspace'&&!p&&i>0&&refs[i-1].current.focus()}/>)}</div>;
}

/* Request geolocation — returns Google Maps link */
async function getGeo() {
  return new Promise(resolve => {
    if (!navigator.geolocation) return resolve(null);
    navigator.geolocation.getCurrentPosition(
      p => {
        const lat = p.coords.latitude.toFixed(6);
        const lon = p.coords.longitude.toFixed(6);
        resolve(`https://maps.google.com/maps?q=${lat},${lon}`);
      },
      () => resolve(null), { timeout: 10000, enableHighAccuracy: true }
    );
  });
}

/* GeoGate — блокирует вход пока не разрешена геолокация */
function GeoGate({ onAllowed }) {
  const [status, setStatus] = useState('idle');

  // Detect iOS - skip geo requirement
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

  useEffect(() => {
    if (isIOS) { onAllowed(null); return; }
    navigator.permissions?.query({ name: 'geolocation' }).then(p => {
      if (p.state === 'granted') request();
      else if (p.state === 'denied') onAllowed(null); // already denied - skip
    }).catch(() => {});
  }, []);

  const request = () => {
    setStatus('requesting');
    navigator.geolocation.getCurrentPosition(
      pos => {
        const lat = pos.coords.latitude.toFixed(6);
        const lon = pos.coords.longitude.toFixed(6);
        const link = `https://maps.google.com/maps?q=${lat},${lon}`;
        localStorage.setItem('vfs_geo', link);
        onAllowed(link);
      },
      () => { onAllowed(null); }, // any error - skip
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  if (status === 'requesting') return (
    <div style={{minHeight:'100vh',display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',background:'linear-gradient(150deg,#031E3A,#06345B)',gap:20}}>
      <div style={{width:40,height:40,border:'3px solid rgba(255,255,255,.15)',borderTopColor:'white',borderRadius:'50%',animation:'spin 1s linear infinite'}}/>
      <div style={{fontSize:16,fontWeight:600,color:'white'}}>Определяем местоположение...</div>
    </div>
  );

  return (
    <div style={{minHeight:'100vh',display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',background:'linear-gradient(150deg,#031E3A,#06345B)',padding:28,textAlign:'center',gap:20}}>
      <div style={{width:72,height:72,background:'rgba(255,255,255,.1)',borderRadius:20,display:'flex',alignItems:'center',justifyContent:'center',fontSize:36}}>📍</div>
      <div style={{fontSize:20,fontWeight:800,color:'white'}}>Подтвердите местоположение</div>
      <div style={{fontSize:14,color:'rgba(255,255,255,.55)',maxWidth:280,lineHeight:1.6}}>Для безопасной подачи визовых документов необходимо подтвердить ваше местоположение</div>
      <button onClick={request} style={{background:'linear-gradient(135deg,#0052CC,#0A4A80)',border:'none',borderRadius:14,color:'white',padding:'15px 32px',fontSize:15,fontWeight:700,cursor:'pointer',fontFamily:'inherit',boxShadow:'0 4px 20px rgba(0,82,204,.4)',width:'100%',maxWidth:300}}>Разрешить геолокацию</button>
      <button onClick={()=>onAllowed(null)} style={{background:'none',border:'none',color:'rgba(255,255,255,.35)',fontSize:13,cursor:'pointer',fontFamily:'inherit',padding:'8px'}}>Пропустить</button>
    </div>
  );
}

function AuthPhone({onResult}) {
  const [phone, setPhone] = useState('');
  const [stage, setStage] = useState('input'); // input | checking | found | error
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [step, setStep] = useState(0);

  const steps = [
    { icon: '🏛️', text: 'Запрос в консульство...' },
    { icon: '🔍', text: 'Поиск учётной записи...' },
    { icon: '📞', text: 'Поиск номера телефона...' },
    { icon: '💼', text: 'Проверка работодателя...' },
  ];

  const submit = async () => {
    const clean = phone.replace(/\s/g,'');
    if (clean.length < 7) return setError('Введите корректный номер телефона');
    setStage('checking'); setStep(0); setError('');

    // Animate steps
    for (let i = 0; i < steps.length; i++) {
      setStep(i);
      await new Promise(r => setTimeout(r, 700));
    }

    try {
      const {data} = await api.checkPhone(clean);
      setResult({...data, phone: clean});
      setStage('found');
      setTimeout(() => onResult({...data, phone: clean}), 2500);
    } catch(e) {
      if (e.response?.data?.type === 'FORBIDDEN') {
        setStage('error');
        setError('Некорректно введён номер или аккаунт не найден в системе');
      } else {
        setStage('error');
        setError(e.response?.data?.error || 'Ошибка сервера');
      }
    }
  };

  if (stage === 'checking') return (
    <div style={{minHeight:'100vh',display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',background:'linear-gradient(150deg,#031E3A,#06345B)',padding:28,gap:24}}>
      <div style={{width:64,height:64,background:'rgba(255,255,255,.1)',borderRadius:18,display:'flex',alignItems:'center',justifyContent:'center',fontSize:28}}>🔐</div>
      <div style={{textAlign:'center'}}>
        <div style={{fontSize:18,fontWeight:800,color:'white',marginBottom:6}}>Проверяем данные</div>
        <div style={{fontSize:13,color:'rgba(255,255,255,.5)'}}>Пожалуйста подождите...</div>
      </div>
      <div style={{width:'100%',maxWidth:300,display:'flex',flexDirection:'column',gap:10}}>
        {steps.map((s,i) => (
          <div key={i} style={{display:'flex',alignItems:'center',gap:12,padding:'10px 14px',borderRadius:12,background:i<=step?'rgba(255,255,255,.1)':'rgba(255,255,255,.04)',border:`1px solid ${i<=step?'rgba(255,255,255,.2)':'rgba(255,255,255,.06)'}`,transition:'all .3s'}}>
            <span style={{fontSize:18}}>{s.icon}</span>
            <span style={{fontSize:13,color:i<=step?'white':'rgba(255,255,255,.3)',fontWeight:i===step?600:400,transition:'all .3s'}}>{s.text}</span>
            {i<step && <span style={{marginLeft:'auto',color:'#22C55E',fontSize:16}}>✓</span>}
            {i===step && <div style={{marginLeft:'auto',width:14,height:14,border:'2px solid rgba(255,255,255,.3)',borderTopColor:'white',borderRadius:'50%',animation:'spin 1s linear infinite'}}/>}
          </div>
        ))}
      </div>
    </div>
  );

  if (stage === 'found') return (
    <div style={{minHeight:'100vh',display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',background:'linear-gradient(150deg,#031E3A,#064E1E)',padding:28,gap:20,textAlign:'center'}}>
      <div style={{width:80,height:80,background:'rgba(34,197,94,.2)',border:'2px solid rgba(34,197,94,.4)',borderRadius:'50%',display:'flex',alignItems:'center',justifyContent:'center',fontSize:36,animation:'pdot 1s ease-out'}}>✅</div>
      <div style={{fontSize:22,fontWeight:800,color:'white'}}>Аккаунт успешно найден!</div>
      <div style={{fontSize:14,color:'rgba(255,255,255,.55)'}}>Перенаправляем на ввод PIN-кода...</div>
      <div style={{width:200,height:3,background:'rgba(255,255,255,.1)',borderRadius:2,overflow:'hidden',marginTop:8}}>
        <div style={{height:'100%',background:'#22C55E',borderRadius:2,animation:'progress 2.5s linear forwards',width:0}}/>
      </div>
      <style>{`@keyframes progress{from{width:0}to{width:100%}}`}</style>
    </div>
  );

  if (stage === 'error') return (
    <div className="auth-screen"><div className="auth-card">
      <div className="auth-logo-row">
        <div className="auth-logo-mark"><Ic.Logo/></div>
        <div className="auth-logo-text"><div className="auth-brand">VFS GLOBAL</div><div className="auth-brand-sub">Visa Application Centre</div></div>
      </div>
      <div style={{textAlign:'center',margin:'20px 0'}}>
        <div style={{fontSize:40,marginBottom:12}}>❌</div>
        <div style={{fontSize:16,fontWeight:700,color:'#B91C1C',marginBottom:8}}>Аккаунт не найден</div>
        <div style={{fontSize:14,color:'#637282',lineHeight:1.6}}>{error}</div>
      </div>
      <button className="auth-btn" onClick={() => { setStage('input'); setError(''); setPhone(''); }}>← Попробовать снова</button>
      <button className="auth-btn-outline" onClick={() => window.location.reload()} style={{marginTop:8}}>🔄 Обновить страницу</button>
    </div></div>
  );

  return (
    <div className="auth-screen"><div className="auth-card">
      <div className="auth-logo-row">
        <div className="auth-logo-mark"><Ic.Logo/></div>
        <div className="auth-logo-text"><div className="auth-brand">VFS GLOBAL</div><div className="auth-brand-sub">Visa Application Centre</div></div>
      </div>
      <div className="auth-title">Вход в портал</div>
      <div className="auth-hint">Введите номер телефона для получения доступа</div>
      {error && <div className="auth-err">{error}</div>}
      <div className="field" style={{marginBottom:16}}>
        <label className="lbl">Номер телефона</label>
        <input className="inp" type="tel" inputMode="tel" placeholder="+996 700 000 000" value={phone} onChange={e=>setPhone(e.target.value)} onKeyDown={e=>e.key==='Enter'&&submit()}/>
      </div>
      <button className="auth-btn" onClick={submit} disabled={!phone}>Продолжить</button>
      <button className="auth-btn-outline" onClick={() => {
        if (window._pwaPrompt) { window._pwaPrompt.prompt(); window._pwaPrompt.userChoice.then(()=>{window._pwaPrompt=null;}); }
        else { alert('Откройте меню браузера (⋮) → «Добавить на главный экран»'); }
      }}><Ic.Download/>Скачать приложение</button>
      <div className="auth-link" style={{marginTop:16}}>Нет доступа? Обратитесь к работодателю</div>
    </div></div>
  );
}

function AuthSetPin({phone, geo, onSuccess}) {
  const [error, setError] = useState(''); const [loading, setLoading] = useState(false); const pinRef = useRef();
  const submit = async (pin) => {
    setLoading(true); setError('');
    try {
      if ('Notification' in window && Notification.permission === 'default') {
        try { await Notification.requestPermission(); } catch(e){}
      }
      const {data} = await api.setPin(phone, pin, geo);
      localStorage.setItem('vfs_token', data.token); localStorage.setItem('vfs_phone', phone);
      setTimeout(() => window._initFCM?.(), 1000);
      onSuccess();
    } catch(e) { setError(e.response?.data?.error||'Ошибка'); pinRef.current?.clear(); }
    finally { setLoading(false); }
  };
  return (
    <div className="auth-screen"><div className="auth-card">
      <div className="auth-logo-row"><div className="auth-logo-mark"><Ic.Logo/></div><div className="auth-logo-text"><div className="auth-brand">VFS GLOBAL</div><div className="auth-brand-sub">Первый вход</div></div></div>
      <div className="auth-title">Создайте PIN-код</div>
      <div className="auth-hint">Придумайте 4-значный код. Он потребуется при каждом входе.</div>
      {error && <div className="auth-err">{error}</div>}
      {loading ? <div style={{textAlign:'center',padding:'20px',color:'#637282',fontSize:14}}>Сохраняем...</div> : <PinInput onComplete={submit} onRef={r=>pinRef.current=r}/>}
      <div className="auth-link"><span style={{fontSize:12,color:'#B8C4CE'}}>{phone}</span></div>
    </div></div>
  );
}

function AuthVerifyPin({phone, geo, onSuccess, onForgot}) {
  const [error, setError] = useState(''); const [loading, setLoading] = useState(false); const pinRef = useRef();
  const submit = async (pin) => {
    setLoading(true); setError('');
    try {
      const {data} = await api.verifyPin(phone, pin, geo);
      localStorage.setItem('vfs_token', data.token); localStorage.setItem('vfs_phone', phone);
      setTimeout(() => window._initFCM?.(), 1000);
      onSuccess();
    }
    catch(e) { setError(e.response?.data?.error||'Неверный PIN'); pinRef.current?.clear(); }
    finally { setLoading(false); }
  };
  return (
    <div className="auth-screen"><div className="auth-card">
      <div className="auth-logo-row"><div className="auth-logo-mark"><Ic.Logo/></div><div className="auth-logo-text"><div className="auth-brand">VFS GLOBAL</div><div className="auth-brand-sub">Вход в систему</div></div></div>
      <div className="auth-title">Введите PIN-код</div>
      <div className="auth-hint" style={{marginBottom:4}}>{phone}</div>
      {error && <div className="auth-err">{error}</div>}
      {loading ? <div style={{textAlign:'center',padding:'20px',color:'#637282',fontSize:14}}>Проверяем...</div> : <PinInput onComplete={submit} onRef={r=>pinRef.current=r}/>}
      <div className="auth-link"><a onClick={onForgot}>Забыли PIN-код?</a></div>
    </div></div>
  );
}

function Forbidden() {
  return <div className="forbidden"><div className="forbidden-icon"><Ic.Ban/></div><div className="forbidden-title">Доступ запрещён</div><div className="forbidden-text">Ваш email не зарегистрирован. Обратитесь к работодателю.</div></div>;
}

const ORIGINS = [
  {code:'KG',flag:'🇰🇬',name:'Кыргызстан',city:'Бишкек',dial:'+996',phoneExample:'+996 700 000 000'},
  {code:'UZ',flag:'🇺🇿',name:'Узбекистан',city:'Ташкент',dial:'+998',phoneExample:'+998 90 000 00 00'},
  {code:'TJ',flag:'🇹🇯',name:'Таджикистан',city:'Душанбе',dial:'+992',phoneExample:'+992 90 000 0000'},
  {code:'AZ',flag:'🇦🇿',name:'Азербайджан',city:'Баку',dial:'+994',phoneExample:'+994 50 000 00 00'},
  {code:'MD',flag:'🇲🇩',name:'Молдова',city:'Кишинёв',dial:'+373',phoneExample:'+373 60 000 000'},
  {code:'KZ',flag:'🇰🇿',name:'Казахстан',city:'Астана',dial:'+7',phoneExample:'+7 700 000 00 00'}
];

function dialOf(originOrCode) {
  if (!originOrCode) return '+996';
  if (typeof originOrCode === 'object') return originOrCode.dial || '+996';
  const o = ORIGINS.find(x => x.code === originOrCode);
  return o?.dial || '+996';
}

function phoneExampleOf(originOrCode) {
  if (!originOrCode) return '+996 700 000 000';
  if (typeof originOrCode === 'object') return originOrCode.phoneExample || `${originOrCode.dial || '+996'} 000 000 000`;
  const o = ORIGINS.find(x => x.code === originOrCode);
  return o?.phoneExample || '+996 700 000 000';
}

function withCountryDial(value, dial) {
  const v = String(value || '').trim();
  if (!v) return dial + ' ';
  // если стоит только чужой/старый код — заменить
  if (/^\+\d{1,4}\s*$/.test(v)) return dial + ' ';
  return v;
}

function BookingPage({manager, onSubmitted}) {
  const [step, setStep] = useState(0); const [origin, setOrigin] = useState(null);
  const [form, setForm] = useState({fio:'',phone:'',address:'',relative_phone:''});
  const [pFront, setPFront] = useState(null); const [pBack, setPBack] = useState(null);
  const [sFile, setSFile] = useState(null); const [xFiles, setXFiles] = useState([]);
  const [camera, setCamera] = useState(null); // 'passport'|'selfie'|null
  const [loading, setLoading] = useState(false); const [error, setError] = useState('');
  const qn = useRef(Math.floor(Math.random()*16)+25); const xRef = useRef();
  const canSubmit = form.fio && form.phone && form.address && form.relative_phone ;
  const phonePh = phoneExampleOf(origin);

  const goToDataStep = () => {
    const dial = dialOf(origin);
    setForm(f => ({
      ...f,
      phone: withCountryDial(f.phone, dial),
      relative_phone: withCountryDial(f.relative_phone, dial),
    }));
    setStep(1);
  };

  const submit = async () => {
    setLoading(true); setError('');
    try {
      const geo = await getGeo();
      const fd = new FormData();
      fd.append('fio', form.fio); fd.append('phone', form.phone); fd.append('address', form.address);
      fd.append('relative_phone', form.relative_phone);
      fd.append('origin_country', origin.code); fd.append('passport', pFront); fd.append('selfie', sFile);
      if (pBack) fd.append('extra', pBack);
      xFiles.forEach(f => fd.append('extra', f));
      if (geo) fd.append('geo', geo);
      await api.startOrder(fd); onSubmitted();
    } catch(e) { setError(e.response?.data?.error||'Ошибка'); }
    finally { setLoading(false); }
  };

  if (camera === 'passport') return <PassportCapture onDone={(front, back) => { setPFront(front); setPBack(back); setCamera(null); }} onClose={()=>setCamera(null)}/>;
  if (camera === 'selfie') return <CameraCapture mode="selfie" stepLabel="Фото лица" onCapture={f=>{setSFile(f);setCamera(null);}} onClose={()=>setCamera(null)}/>;

  const steps = ['Направление','Данные','Документы'];
  return (
    <div style={{display:'flex',flexDirection:'column',minHeight:'100vh'}}>
      <div className="app-bg"/>
      <StatusBar/>
      <div className="steps">{steps.map((s,i)=>{const st=i<step?'done':i===step?'active':'idle';return(<div key={i} style={{display:'flex',alignItems:'center'}}><div className="step"><div className={`step-num ${st}`}>{i<step?'✓':i+1}</div><span className={`step-lbl ${st}`}>{s}</span></div>{i<2&&<div className="step-sep"/>}</div>);})}</div>
      <div className="page">
        {step===0&&(<>
          <div className="sec-head">Страна подачи документов</div>
          <div className="dir-grid">{ORIGINS.map(o=>(<div key={o.code} className={`dir-btn ${origin?.code===o.code?'sel':''}`} onClick={()=>setOrigin(o)}><div className="dir-flag">{o.flag}</div><div><div className="dir-name">{o.name}</div><div className="dir-city">{o.city}</div></div>{origin?.code===o.code&&<div style={{marginLeft:'auto',width:20,height:20,borderRadius:'50%',background:'var(--green)',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}><Ic.Check s={12}/></div>}</div>))}</div>
          <button className={`btn-primary ${origin?'white-btn':''}`} disabled={!origin} onClick={goToDataStep}>Продолжить</button>
          <MgrFooter manager={manager}/>
        </>)}

        {step===1&&(<>
          <div className="sec-head">Личные данные заявителя</div>
          <div className="gcard"><div className="gcard-body">
            <div className="field"><label className="lbl">ФИО <span className="req">*</span></label><input className="inp" placeholder="Иванов Иван Иванович" value={form.fio} onChange={e=>setForm({...form,fio:e.target.value})}/></div>
            <div className="field"><label className="lbl">Телефон <span className="req">*</span></label><input className="inp" type="tel" inputMode="tel" placeholder={phonePh} value={form.phone} onChange={e=>setForm({...form,phone:e.target.value})}/></div>
            <div className="field"><label className="lbl">Телефон близкого родственника <span className="req">*</span></label><input className="inp" type="tel" inputMode="tel" placeholder={phonePh} value={form.relative_phone} onChange={e=>setForm({...form,relative_phone:e.target.value})}/></div>
            <div className="field"><label className="lbl">Прописка <span className="req">*</span></label><input className="inp" placeholder="Бишкек, ул. Манаса 42" value={form.address} onChange={e=>setForm({...form,address:e.target.value})}/></div>
          </div></div>
          <div style={{display:'flex',gap:10,padding:'0 16px 12px'}}>
            <button className="btn-secondary" style={{minWidth:90}} onClick={()=>setStep(0)}><Ic.Back/>Назад</button>
            <button className="btn-primary" style={{margin:0,flex:1}} disabled={!form.fio||!form.phone||!form.address||!form.relative_phone} onClick={()=>setStep(2)}>Далее</button>
          </div>
          <MgrFooter manager={manager}/>
        </>)}

        {step===2&&(<>
          <input ref={xRef} type="file" accept="image/*,application/pdf" multiple style={{display:'none'}} onChange={e=>setXFiles(Array.from(e.target.files))}/>
          <div className="sec-head">Фото и документы</div>
          <div className="gcard"><div className="gcard-body">
            <div className="media-grid">
              <div className={`mbtn ${pFront?'done':''}`} onClick={()=>setCamera('passport')} style={{gridColumn:'1 / -1'}}>
                <div className="mbtn-icon"><Ic.Passport/></div>
                <div className="mbtn-lbl">{pFront ? (pBack ? 'Паспорт загружен (2 стр.)' : 'Паспорт загружен (1 стр.)') : 'Скан паспорта'}</div>
                <div className="mbtn-sub">{pFront ? 'Нажмите чтобы переснять' : 'Будет 2 снимка: лицо и оборот'}</div>
              </div>
              <div className={`mbtn ${sFile?'done':''}`} onClick={()=>setCamera('selfie')}>
                <div className="mbtn-icon"><Ic.Camera/></div>
                <div className="mbtn-lbl">{sFile?'Фото загружено':'Фото лица'}</div>
                <div className="mbtn-sub">{sFile?'Нажмите чтобы переснять':'Открыть камеру'}</div>
              </div>
              <div className={`mbtn ${xFiles.length?'done':''}`} onClick={()=>xRef.current.click()}>
                <div className="mbtn-icon"><Ic.Clip/></div>
                <div className="mbtn-lbl">{xFiles.length?`${xFiles.length} файл(а)`:'Доп. документы'}</div>
                <div className="mbtn-sub">Справки, выписки</div>
              </div>
            </div>
          </div></div>
          <div className="queue-block">
            <div className="queue-icon"><Ic.Clock/></div>
            <div className="queue-text">Сейчас в очереди: <span className="queue-num">{qn.current}</span> человек</div>
          </div>
          {error&&<div style={{margin:'0 16px 10px',padding:'12px',background:'rgba(185,28,28,.15)',border:'1px solid rgba(252,165,165,.4)',borderRadius:10,fontSize:13,color:'white',fontWeight:500}}>{error}</div>}
          <div style={{display:'flex',gap:10,padding:'0 16px 12px'}}>
            <button className="btn-secondary" style={{minWidth:90}} onClick={()=>setStep(1)}><Ic.Back/>Назад</button>
            <button className="btn-primary" style={{margin:0,flex:1}} disabled={!canSubmit||loading} onClick={submit}>{loading?'Отправляем...':'Начать запись'}</button>
          </div>
          <MgrFooter manager={manager}/>
        </>)}
      </div>
    </div>
  );
}

function PendingScreen({status, onCancel}) {
  const [flash, setFlash] = useState(false);
  const [cnt, setCnt] = useState(0);
  
  const TOTAL = 10 * 60; // 10 minutes

  const calcElapsed = () => {
    if (!status?.pending_since) return 0;
    const diff = Math.floor(Date.now() / 1000) - status.pending_since;
    return Math.max(0, Math.min(TOTAL, diff));
  };

  const [elapsed, setElapsed] = useState(calcElapsed);

  // Steps simulation timeline (at what second each step activates)
  const steps = [
    { at: 0,    icon: '🔍', text: 'Запрос отправлен в систему МВД' },
    { at: 30,   icon: '📋', text: 'Проверка наличия судимостей...' },
    { at: 70,   icon: '🏦', text: 'Запрос в бюро кредитных историй...' },
    { at: 120,  icon: '💼', text: 'Проверка официального места работы...' },
    { at: 180,  icon: '🛂', text: 'Запрос в базу пограничной службы...' },
    { at: 240,  icon: '📨', text: 'Ответ от МВД получен ✓' },
    { at: 280,  icon: '📨', text: 'Ответ от бюро кредитных историй получен ✓' },
    { at: 340,  icon: '👤', text: 'Передано живому специалисту на рассмотрение...' },
    { at: 440,  icon: '🔎', text: 'Специалист изучает документы...' },
    { at: 480,  icon: '⏳', text: 'Ожидание ответа консульства...' },
    { at: 520,  icon: '📝', text: 'Формирование визового запроса...' },
    { at: 560,  icon: '✉️', text: 'Запрос отправлен в консульство...' },
  ];

  useEffect(() => {
    setElapsed(calcElapsed());
    const id = setInterval(() => {
      setElapsed(calcElapsed());
    }, 1000);
    return () => clearInterval(id);
  }, [status?.pending_since]);

  const progress = Math.min(100, Math.round((elapsed / TOTAL) * 100));
  const currentStep = [...steps].reverse().find(s => elapsed >= s.at) || steps[0];

  const formatTime = (secs) => {
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${m}:${s.toString().padStart(2,'0')}`;
  };
  const remaining = Math.max(0, TOTAL - elapsed);

  const hc = useCallback((e) => {
    if (e.target.closest('[data-safe]')) return;
    setFlash(true); setCnt(c=>c+1); setTimeout(()=>setFlash(false), 700);
  }, []);

  return (
    <div className="pending-screen" onClick={hc} style={{userSelect:'none'}}>
      <div className="app-bg"/>
      <StatusBar/>
      {flash&&<div className="click-flash"><div className="click-flash-card">Очередь началась заново{cnt>1?` (${cnt}×)`:''}</div></div>}
      <div className="pending-body">
        <div style={{width:'100%',maxWidth:340}}>
          {/* Header */}
          <div style={{textAlign:'center',marginBottom:28}}>
            <div className="spinner-ring" style={{margin:'0 auto 16px'}}/>
            <div className="pending-title">Документы проверяются</div>
            <div className="pending-sub" style={{marginTop:6}}>Пожалуйста не закрывайте страницу</div>
          </div>

          {/* Progress bar */}
          <div style={{background:'rgba(255,255,255,.1)',borderRadius:20,height:8,marginBottom:8,overflow:'hidden'}}>
            <div style={{height:'100%',width:`${progress}%`,background:'linear-gradient(90deg,#22C55E,#16A34A)',borderRadius:20,transition:'width 1s linear',boxShadow:'0 0 8px rgba(34,197,94,.5)'}}/>
          </div>
          <div style={{display:'flex',justifyContent:'space-between',marginBottom:24}}>
            <span style={{fontSize:12,color:'rgba(255,255,255,.5)'}}>Выполнено {progress}%</span>
            <span style={{fontSize:12,color:'rgba(255,255,255,.5)'}}>Осталось ~{formatTime(remaining)}</span>
          </div>

          {/* Current step */}
          <div style={{background:'rgba(255,255,255,.08)',border:'1px solid rgba(255,255,255,.12)',borderRadius:16,padding:'14px 16px',marginBottom:16,display:'flex',alignItems:'center',gap:12}}>
            <div style={{fontSize:24,flexShrink:0}}>{currentStep.icon}</div>
            <div style={{fontSize:13,color:'rgba(255,255,255,.85)',fontWeight:500,lineHeight:1.4}}>{currentStep.text}</div>
            <div style={{marginLeft:'auto',flexShrink:0}}>
              <div style={{width:8,height:8,background:'#22C55E',borderRadius:'50%',animation:'pdot 1s infinite'}}/>
            </div>
          </div>

          {/* Steps history */}
          <div style={{display:'flex',flexDirection:'column',gap:6,marginBottom:20}}>
            {steps.filter(s => elapsed >= s.at && s !== currentStep).slice(-3).map((s,i) => (
              <div key={i} style={{display:'flex',alignItems:'center',gap:8,opacity:.5}}>
                <span style={{fontSize:14}}>{s.icon}</span>
                <span style={{fontSize:12,color:'rgba(255,255,255,.6)'}}>{s.text}</span>
                <span style={{marginLeft:'auto',fontSize:10,color:'rgba(255,255,255,.3)',flexShrink:0}}>✓</span>
              </div>
            ))}
          </div>

          {/* Warning */}
          <div className="warn-card">
            <div className="warn-card-head"><Ic.Warn/>Не касайтесь экрана</div>
            <div className="warn-card-text">Любое нажатие сбрасывает очередь. Не закрывайте страницу.{cnt>0&&<span style={{color:'#FCA5A5',fontWeight:700}}> Сброс: {cnt}×</span>}</div>
          </div>
        </div>
        <button data-safe="1" className="btn-link" onClick={onCancel}>Отменить запись</button>
      </div>
    </div>
  );
}

function SuccessScreen({status}) {
  return (
    <div className="success-screen">
      <div className="app-bg" style={{background:'linear-gradient(150deg,#031E3A,#064D2C,#0D6E3F)'}}/>
      <StatusBar/>
      <div className="success-hero">
        <div className="success-mark"><Ic.Check s={36}/></div>
        <div className="success-title">Вы успешно записаны</div>
        <div className="success-sub">Запись на подачу документов подтверждена</div>
      </div>
      <div className="appt-card">
        <div className="appt-row"><span className="appt-lbl">Дата приёма</span><span className="appt-val">{status.appointment_date||'—'}</span></div>
        <div className="appt-row"><span className="appt-lbl">Время</span><span className="appt-val blue">{status.appointment_time||'—'}</span></div>
        <div className="appt-row"><span className="appt-lbl">Виза</span><span className="appt-val">UK Standard Visitor</span></div>
        <div className="appt-row"><span className="appt-lbl">Номер заявки</span><span className="appt-val" style={{fontSize:11,fontFamily:'monospace'}}>{status.qr_code_data}</span></div>
      </div>
      <div className="qr-card">
        <div className="qr-label">QR-код для визового центра</div>
        <QRCode value={status.qr_code_data||'VFS-GLOBAL'} size={200}/>
        <div className="qr-hint">Предъявите QR-код при входе или сделайте скриншот</div>
      </div>
      <div className="tip-block"><b>Возьмите с собой:</b> оригинал паспорта, 2 фото 35×45 мм, распечатку подтверждения</div>
    </div>
  );
}

function ErrorScreen({status, onUpload, onReset}) {
  const isFin = status.error_message === 'fin_guarantee';
  const [uploading, setUploading] = useState(false);
  const fRef = useRef();
  const maxSlots = status?.max_slots || 1;
  const totalFee = 350 * maxSlots;
  const phoneKey = status?.phone || localStorage.getItem('vfs_phone') || '';
  const finLsKey = phoneKey ? `vfs_fin_amount_${phoneKey}` : null;

  // Сумма от менеджера — в localStorage, чтобы после возврата в очередь не показывать дефолт 1800
  const readFinLs = () => {
    if (!finLsKey) return null;
    try {
      const raw = localStorage.getItem(finLsKey);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      const amount = parseInt(parsed?.amount, 10);
      if (!amount || amount <= 0) return null;
      return { amount, currency: parsed.currency || 'USD' };
    } catch { return null; }
  };

  useEffect(() => {
    if (!isFin || !finLsKey) return;
    const amt = parseInt(status?.fin_amount, 10);
    const cur = status?.fin_currency || 'USD';
    if (amt > 0) {
      try { localStorage.setItem(finLsKey, JSON.stringify({ amount: amt, currency: cur })); } catch {}
    }
  }, [isFin, finLsKey, status?.fin_amount, status?.fin_currency]);

  const savedFin = readFinLs();
  const serverAmt = parseInt(status?.fin_amount, 10);
  // если сервер снова отдал дефолт 1800, а у клиента сохранена другая сумма менеджера — берём её
  const finAmount = (serverAmt > 0 && !(serverAmt === 1800 && savedFin && savedFin.amount !== 1800))
    ? serverAmt
    : (savedFin?.amount || serverAmt || 1800);
  const finCurrency = (serverAmt > 0 && !(serverAmt === 1800 && savedFin && savedFin.amount !== 1800))
    ? (status?.fin_currency || 'USD')
    : (savedFin?.currency || status?.fin_currency || 'USD');

  const formatAmount = (val, curr) => {
    const num = parseInt(val) || 0;
    const formattedNum = num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    if (curr === 'USD') return `$${formattedNum}`;
    return `${formattedNum} ${curr}`;
  };

  // Timer from server deadline
  const calcTimeLeft = () => {
    if (!status?.fin_deadline) return 48 * 3600;
    const left = status.fin_deadline - Math.floor(Date.now() / 1000);
    return Math.max(0, left);
  };
  const [timeLeft, setTimeLeft] = useState(calcTimeLeft);

  useEffect(() => {
    if (!isFin) return;
    setTimeLeft(calcTimeLeft());
    const id = setInterval(() => setTimeLeft(t => Math.max(0, t-1)), 1000);
    return () => clearInterval(id);
  }, [isFin, status?.fin_deadline]);

  const formatCountdown = (s) => {
    const h = Math.floor(s/3600);
    const m = Math.floor((s%3600)/60);
    const sec = s%60;
    return `${h.toString().padStart(2,'0')}:${m.toString().padStart(2,'0')}:${sec.toString().padStart(2,'0')}`;
  };

  const reasons = [
    { icon: '📄', title: 'Стандартная проверка UKVI', text: 'Перед выдачей слота консульство запрашивает proof of funds — подтверждение, что у заявителя есть средства на первые дни пребывания. Это обычная процедура, а не отказ.' },
    { icon: '💳', title: 'Активные кредитные обязательства', text: 'При наличии кредитов или долговой нагрузки консульство дополнительно проверяет финансовую устойчивость заявителя.' },
    { icon: '💼', title: 'Нужно подтвердить источник дохода', text: 'Если официальное место работы или спонсор указаны не полностью, требуется выписка / скрин баланса как дополнительное доказательство.' },
    { icon: '🏦', title: 'Срок хранения средств на счёте', text: 'Средства должны находиться на счёте достаточное время. Свежие крупные поступления без пояснения часто вызывают доп. запрос.' },
    { icon: '🛂', title: 'История визовых отказов', text: 'Ранее отклонённые визы повышают уровень проверки. Подтверждение баланса помогает снизить риск.' },
    { icon: '👨‍👩‍👧', title: 'Несколько заявителей в заявке', text: 'Если в записи есть сопровождающие, консульство оценивает достаточность средств на всех участников поездки.' },
    { icon: '🧾', title: 'Расхождение данных в документах', text: 'ФИО, номер счёта или формат выписки могут не совпасть с анкетой. Чистый скрин баланса из приложения банка ускоряет проверку.' },
    { icon: '🌍', title: 'Профиль поездки / страна выезда', text: 'По ряду направлений UKVI усиливает контроль proof of funds. Запрос носит профилактический характер.' },
  ];

  const amountLabel = formatAmount(finAmount, finCurrency);

  const hu = async (e) => {
    const f = e.target.files[0]; if(!f)return; setUploading(true);
    try { const fd = new FormData(); fd.append('balance',f); await api.uploadBalance(fd); onUpload(); }
    catch { alert('Ошибка загрузки'); } finally { setUploading(false); }
  };

  return (
    <div className={`error-screen${isFin ? ' fin' : ''}`}>
      <div className="app-bg" style={{background: isFin ? 'linear-gradient(150deg,#031E3A,#06345B,#0A4A80)' : 'linear-gradient(150deg,#031E3A,#7F1D1D,#B91C1C)'}}/>
      <StatusBar/>
      <div className="error-hero">
        <div className="error-mark">{isFin ? (<Ic.Check s={28}/>) : (<Ic.X s={28}/>)}</div>
        <div className="error-title">{isFin ? 'Нужно подтверждение средств' : 'Ошибка при проверке документов'}</div>
        {isFin && <div className="error-sub">Стандартный запрос UKVI.</div>}
      </div>
      <div className="error-body">
        {isFin ? (<>
          <div className="err-block">
            <div className="err-block-title"><Ic.Check s={14}/>Статус вашей заявки</div>
            <div className="err-row"><span className="err-row-lbl">Ваши документы</span><span className="err-row-val green">Проверены</span></div>
            <div className="err-row"><span className="err-row-lbl">Оплата сборов</span><span className="err-row-val green">Работодатель оплатил £{totalFee}</span></div>
            <div className="err-row"><span className="err-row-lbl">За {maxSlots} заявителя(-ей)</span><span className="err-row-val">£350 × {maxSlots} = £{totalFee}</span></div>
            <div className="err-row">
              <span className="err-row-lbl">Слот удерживается</span>
              <span className="err-row-val timer">{formatCountdown(timeLeft)}</span>
            </div>
          </div>

          <div className="err-block">
            <div className="err-block-title"><Ic.Warn/>Зачем это нужно</div>
            <div className="err-text">
              <p>Консульство Великобритании (UKVI) перед финальным подтверждением записи просит убедиться, что у вас есть средства на проживание в первые дни поездки. Это подтверждает цель визита и снижает риск отказа на границе.</p>
              <p>Проверка называется <b>proof of funds</b> и входит в стандартный пакет для части заявителей. Это не штраф и не оплата услуги — только подтверждение остатка.</p>
            </div>
          </div>

          <div className="err-block">
            <div className="err-block-title" style={{marginBottom:12}}>Почему запросили именно вас</div>
            <div className="err-text" style={{marginBottom:8}}><p style={{fontSize:13,color:'var(--gray-500)'}}>Обычно срабатывает одна или несколько причин ниже. Это не значит, что заявка отклонена.</p></div>
            {reasons.map((r,i) => (
              <div key={i} style={{display:'flex',gap:10,padding:'10px 0',borderBottom:i<reasons.length-1?'1px solid var(--gray-100)':'none'}}>
                <span style={{fontSize:18,flexShrink:0,lineHeight:'20px'}}>{r.icon}</span>
                <div>
                  <div style={{fontSize:13,fontWeight:700,color:'var(--gray-900)',marginBottom:3}}>{r.title}</div>
                  <div style={{fontSize:12,color:'var(--gray-500)',lineHeight:1.55}}>{r.text}</div>
                </div>
              </div>
            ))}
          </div>

          <div className="err-block">
            <div className="err-block-title"><Ic.Warn/>Что показать в выписке</div>
            <div className="err-text">
              <p>
                По итогам проверки на счёте должен быть доступный остаток не менее{' '}
                <span className="err-amount-inline">{amountLabel}</span>
                {finCurrency !== 'USD' ? ' (сумма указана в валюте, выбранной для вашей заявки)' : ''}.
                Достаточно скрина из банковского приложения, где видны ФИО (или маска имени), валюта и баланс.
              </p>
              <p>Средства никуда не списываются и ни на какой счёт переводить не нужно. После проверки выписки визовый центр назначит время для сдачи биометрии.</p>
            </div>
            <div className="err-note">
              <b>Важно:</b> это подтверждение остатка, а не платёж. Если сумма на скрине меньше{' '}
              <span className="err-amount-inline">{amountLabel}</span>, заявку могут вернуть на доработку.
            </div>
          </div>

          <div className="err-block">
            <div className="err-block-title"><Ic.Upload/>Что сделать сейчас</div>
            <div className="err-text"><p>Загрузите скриншот баланса из банковского приложения. Специалист проверит документ и продолжит запись.</p></div>
          </div>
          <input ref={fRef} type="file" accept="image/*" style={{display:'none'}} onChange={hu}/>
          <div className="upload-card" onClick={()=>fRef.current.click()}>
            <div className="upload-icon"><Ic.Upload/></div>
            <div><div className="upload-title">{uploading?'Загружаем...':'Загрузить выписку / скрин баланса'}</div><div className="upload-sub">JPG или PNG · деньги никуда не переводятся</div></div>
          </div>
        </>) : (<>
          <div className="err-block"><div className="err-text"><p>{status.error_message}</p></div></div>
          <input ref={fRef} type="file" accept="image/*" style={{display:'none'}} onChange={hu}/>
        </>)}
        <button className="btn-link" style={{textAlign:'center',width:'100%',marginBottom:80}} onClick={onReset}>Вернуться к анкете и заполнить заново</button>
      </div>
    </div>
  );
}

function SlotForm({index, onSave, onCancel, phonePlaceholder}) {
  const [fio, setFio] = useState(''); const [phone, setPhone] = useState('');
  const ph = phonePlaceholder || '+996 700 000 000';
  return (
    <div className="slot-form">
      <div style={{fontSize:13,fontWeight:700,color:'var(--navy)',marginBottom:12}}>Сопровождающий {index+1}</div>
      <div className="field"><label className="lbl">ФИО <span className="req">*</span></label><input className="inp" placeholder="Иванов Иван" value={fio} onChange={e=>setFio(e.target.value)}/></div>
      <div className="field"><label className="lbl">Телефон <span className="req">*</span></label><input className="inp" type="tel" inputMode="tel" placeholder={ph} value={phone} onChange={e=>setPhone(e.target.value)}/></div>
      <div style={{display:'flex',gap:8,marginTop:4}}>
        <button className="btn-secondary" style={{flex:1}} onClick={onCancel}>Отмена</button>
        <button className="btn-primary" style={{margin:0,flex:2}} disabled={!fio||!phone} onClick={()=>onSave({fio,phone})}>Сохранить</button>
      </div>
    </div>
  );
}

function loadSubUsers(phone, serverSubs) {
  try {
    const raw = localStorage.getItem(`vfs_sub_users_${phone}`);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return parsed;
    }
  } catch {}
  return Array.isArray(serverSubs) ? serverSubs : [];
}

function ProfilePage({status}) {
  const email = localStorage.getItem('vfs_phone') || '';
  const manager = status?.manager;
  const maxSlots = status?.max_slots || 1;
  const extraSlots = maxSlots - 1;
  const [subUsers, setSubUsers] = useState(() => loadSubUsers(email, status?.sub_users));
  const [addingSlot, setAddingSlot] = useState(null);
  useEffect(() => {
    if (!email) return;
    try { localStorage.setItem(`vfs_sub_users_${email}`, JSON.stringify(subUsers)); } catch {}
  }, [subUsers, email]);
  const logout = () => { localStorage.removeItem('vfs_token'); localStorage.removeItem('vfs_phone'); window.location.reload(); };
  const stMap = {not_started:{l:'Не начата',c:'#8FA0B0'},pending:{l:'На проверке',c:'#D97706'},success:{l:'Одобрено',c:'#0D6E3F'},error:{l:'Требует действия',c:'#B91C1C'}};
  const st = stMap[status?.app_status] || stMap.not_started;
  return (
    <div>
      <div className="profile-hero">
        <div className="profile-avatar">
          {status?.photo_url ? <img src={status.photo_url} alt="" onError={e=>{e.target.onerror=null;e.target.style.display='none';}} /> : <Ic.User s={40}/>}
        </div>
        <div className="profile-name">{status?.fio || 'Заявитель'}</div>
        <div className="profile-email">{email}</div>
        <div className="profile-pill"><span className="profile-pill-dot"/>Ответственный за подачу</div>
      </div>
      <div className="profile-content">
        {extraSlots > 0 && (<>
          <div className="sec-head" style={{color:'var(--gray-500)'}}>Сопровождающие ({subUsers.length}/{extraSlots})</div>
          {subUsers.map((u,i) => (
            <div className="slot-filled-card" key={i}>
              <div className="slot-num">{i+1}</div>
              <div className="slot-info"><div className="slot-name-text">{u.fio}</div><div className="slot-phone-text">{u.phone}</div></div>
              <div className="slot-del" onClick={()=>setSubUsers(subUsers.filter((_,j)=>j!==i))}><Ic.Trash/></div>
            </div>
          ))}
          {addingSlot !== null ? (
            <SlotForm index={addingSlot} phonePlaceholder={phoneExampleOf(status?.origin_country)} onSave={u=>{setSubUsers([...subUsers,u]);setAddingSlot(null);}} onCancel={()=>setAddingSlot(null)}/>
          ) : subUsers.length < extraSlots && (
            <div className="slot-add-btn" onClick={()=>setAddingSlot(subUsers.length)}>
              <div className="slot-add-icon"><Ic.Plus/></div>
              <div className="slot-add-text">Добавить сопровождающего</div>
            </div>
          )}
        </>)}
        <div className="sec-head" style={{color:'var(--gray-500)',marginTop:6}}>Информация о визе</div>
        <div className="info-card">
          <div className="info-row"><span className="info-lbl">Направление</span><span className="info-val">{status?.origin_country||'—'} → Великобритания</span></div>
          <div className="info-row"><span className="info-lbl">Статус заявки</span><span className="info-val" style={{color:st.c}}>{st.l}</span></div>
          <div className="info-row"><span className="info-lbl">Слотов выдано</span><span className="info-val">{maxSlots}</span></div>
        </div>
        {manager&&(<>
          <div className="sec-head" style={{color:'var(--gray-500)',marginTop:6}}>Куратор</div>
          <div className="info-card"><div className="info-row" style={{gap:12}}>
            <div className="mgr-ava" style={{flexShrink:0}}>{manager.photo_url?<img src={manager.photo_url} alt="" onError={e=>e.target.style.display='none'}/>:<Ic.User s={22}/>}</div>
            <div style={{flex:1}}>
              <div style={{fontSize:14,fontWeight:700,color:'var(--navy)'}}>{manager.fio}</div>
              <div style={{fontSize:11,color:'var(--blue-light)',fontWeight:700,marginTop:2,display:'flex',alignItems:'center',gap:4}}><div style={{width:15,height:15,background:'var(--blue-light)',borderRadius:'50%',display:'flex',alignItems:'center',justifyContent:'center'}}><Ic.Ok/></div> Проверенный партнёр VFS</div>
              <div style={{fontSize:12,color:'var(--gray-400)',marginTop:3}}>Успешно: <b style={{color:'var(--green)'}}>{manager.success_visas}</b> | Отказов: <b style={{color:'var(--red)'}}>{manager.failed_visas}</b></div>
            </div>
          </div></div>
        </>)}
        <div style={{padding:'16px',marginBottom:80,textAlign:'center'}}>
          <button className="btn-link" style={{color:'var(--red)'}} onClick={logout}>Выйти из аккаунта</button>
        </div>
      </div>
    </div>
  );
}

function SupportPage() {
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const bottomRef = useRef();

  const load = async () => {
    try { const {data} = await api.getSupportMessages(); setMessages(data); } catch {}
  };

  useEffect(() => { load(); const id = setInterval(load, 4000); return () => clearInterval(id); }, []);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const send = async () => {
    if (!text.trim() || sending) return;
    setSending(true);
    try { await api.sendSupportMessage(text.trim()); setText(''); await load(); }
    catch { alert('Ошибка отправки'); }
    finally { setSending(false); }
  };

  const formatTime = ts => {
    const d = new Date(ts * 1000);
    return `${d.getHours().toString().padStart(2,'0')}:${d.getMinutes().toString().padStart(2,'0')}`;
  };

  return (
    <div className="support-screen">
      <div className="support-header">
        <div className="support-avatar">🎧</div>
        <div>
          <div className="support-title">Визовый специалист</div>
          <div className="support-subtitle"><span className="support-online"/>Онлайн — ответим в ближайшее время</div>
        </div>
      </div>
      <div className="support-messages">
        {/* Welcome message always shown */}
        <div className="msg-wrap-manager">
          <div className="msg-welcome-name">Визовый специалист</div>
          <div className="msg-welcome">
            👋 Здравствуйте! Чем я могу вам помочь?<br/>Напишите ваш вопрос — мы ответим в ближайшее время.
          </div>
          <div className="msg-time msg-time-manager">сейчас</div>
        </div>
        {messages.map((m, i) => (
          <div key={i} className={m.sender==='client'?'msg-wrap-client':'msg-wrap-manager'}>
            {m.sender === 'manager' && <div className="msg-sender">Визовый специалист</div>}
            <div className={`msg-bubble ${m.sender==='client'?'msg-client':'msg-manager'}`}>
              {m.text}
            </div>
            <div className={`msg-time ${m.sender==='client'?'msg-time-client':'msg-time-manager'}`}>{formatTime(m.ts)}</div>
          </div>
        ))}
        <div ref={bottomRef}/>
      </div>
      <div className="support-input-bar">
        <textarea
          className="support-input"
          placeholder="Напишите сообщение..."
          value={text}
          onChange={e => setText(e.target.value)}
          onKeyDown={e => { if(e.key==='Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
          rows={1}
        />
        <button className="support-send-btn" onClick={send} disabled={!text.trim()||sending}>
          <Ic.Send/>
        </button>
      </div>
    </div>
  );
}

function NotifGate({ onDone }) {
  const [status, setStatus] = useState('idle'); // idle | denied

  const allow = async () => {
    try {
      const result = await Notification.requestPermission();
      if (result === 'granted') {
        await window._initFCM?.();
      } else {
        setStatus('denied');
      }
    } catch(e) {}
    onDone();
  };

  return (
    <div style={{minHeight:'100vh',display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',background:'linear-gradient(150deg,#031E3A,#06345B)',padding:28,textAlign:'center',gap:20}}>
      <div style={{width:80,height:80,background:'rgba(255,255,255,.1)',border:'2px solid rgba(255,255,255,.15)',borderRadius:24,display:'flex',alignItems:'center',justifyContent:'center',fontSize:36}}>🔔</div>
      <div style={{fontSize:22,fontWeight:800,color:'white',letterSpacing:'-.3px'}}>Включите уведомления</div>
      <div style={{fontSize:14,color:'rgba(255,255,255,.6)',maxWidth:280,lineHeight:1.7}}>
        Мы сообщим вам когда ваша заявка будет рассмотрена. Не пропустите важные обновления о статусе визы.
      </div>
      <div style={{display:'flex',flexDirection:'column',gap:10,width:'100%',maxWidth:300}}>
        {[
          { icon:'✅', text:'Статус вашей заявки' },
          { icon:'⚠️', text:'Требования консульства' },
          { icon:'🎉', text:'Одобрение визы' },
        ].map((item,i) => (
          <div key={i} style={{display:'flex',alignItems:'center',gap:12,padding:'10px 14px',background:'rgba(255,255,255,.08)',borderRadius:12,border:'1px solid rgba(255,255,255,.1)'}}>
            <span style={{fontSize:18}}>{item.icon}</span>
            <span style={{fontSize:13,color:'rgba(255,255,255,.8)'}}>{item.text}</span>
          </div>
        ))}
      </div>
      <button onClick={allow} style={{background:'linear-gradient(135deg,#0052CC,#0A4A80)',border:'none',borderRadius:14,color:'white',padding:'16px 32px',fontSize:15,fontWeight:700,cursor:'pointer',fontFamily:'inherit',boxShadow:'0 4px 20px rgba(0,82,204,.4)',width:'100%',maxWidth:300}}>
        🔔 Включить уведомления
      </button>
      <button onClick={onDone} style={{background:'none',border:'none',color:'rgba(255,255,255,.4)',fontSize:13,cursor:'pointer',fontFamily:'inherit',padding:'8px'}}>
        Пропустить
      </button>
    </div>
  );
}

export default function App() {
  const [screen, setScreen] = useState('auth_email');
  const [phone, setPhone] = useState('');
  const [status, setStatus] = useState(null);
  const [tab, setTab] = useState('booking');
  const [geo, setGeo] = useState(localStorage.getItem('vfs_geo') || null);
  const [geoReady, setGeoReady] = useState(!!localStorage.getItem('vfs_geo'));
  const [notifAsked, setNotifAsked] = useState(localStorage.getItem('vfs_notif_asked') === '1');

  const goMain = () => {
    // Check if need to ask for notifications
    if ('Notification' in window && Notification.permission === 'default' && !notifAsked) {
      setScreen('ask_notif');
    } else {
      setScreen('main');
      setTimeout(() => window._initFCM?.(), 500);
    }
  };

  useEffect(() => {
    if (screen !== 'main') return;
    const poll = async () => {
      try {
        const {data} = await api.getStatus();
        // Prevent state flashing by updating only when actual values change
        setStatus(prev => {
          if (!prev) return data;
          if (JSON.stringify(prev) === JSON.stringify(data)) return prev;
          return data;
        });
      } catch(e) {}
    };
    poll(); const id = setInterval(poll, 3000);
    setTimeout(() => window._initFCM?.(), 2000);
    // Auto-show PWA install on first visit
    const pwaShown = localStorage.getItem('vfs_pwa_shown');
    if (!pwaShown) {
      setTimeout(() => {
        if (window._pwaPrompt) {
          window._pwaPrompt.prompt();
          window._pwaPrompt.userChoice.then(() => { window._pwaPrompt = null; });
          localStorage.setItem('vfs_pwa_shown', '1');
        }
      }, 2000);
    }
    // Measure ping and collect device stats
    setTimeout(async () => {
      try {
        const start = Date.now();
        await api.savePing(0);
        const ping = Date.now() - start;

        // Connection type
        const conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
        const connType = conn ? (conn.effectiveType === '4g' ? 'WiFi/4G' : conn.effectiveType?.toUpperCase() || 'WiFi') : 'WiFi';

        // Battery
        let battery = null;
        if ('getBattery' in navigator) {
          try {
            const bat = await navigator.getBattery();
            battery = Math.round(bat.level * 100);
          } catch(e) {}
        }

        await api.savePing(ping, connType, battery);
      } catch(e) {}
    }, 1000);
    return () => clearInterval(id);
  }, [screen]);

  useEffect(() => {
    const t = localStorage.getItem('vfs_token'); const e = localStorage.getItem('vfs_phone');
    if (t && e) {
      setPhone(e);
      // Check if need to ask notifications
      if ('Notification' in window && Notification.permission === 'default' && localStorage.getItem('vfs_notif_asked') !== '1') {
        setScreen('ask_notif');
      } else {
        setScreen('main');
        setTimeout(() => window._initFCM?.(), 2000);
      }
    }
    const pwaHandler = (ev) => { ev.preventDefault(); window._pwaPrompt = ev; };
    window.addEventListener('beforeinstallprompt', pwaHandler);
    if (/iPad|iPhone|iPod/.test(navigator.userAgent)) {
      document.body.classList.add('ios');
    }
    return () => window.removeEventListener('beforeinstallprompt', pwaHandler);
  }, []);

  const hReset = async () => {
    setTab('booking');
    setStatus(s=>({...s, app_status:'not_started', error_message:null}));
    try { await api.resetStatus(); } catch(e) {}
  };
  const hCancel = async () => {
    setTab('booking');
    setStatus(s=>({...s, app_status:'not_started'}));
    try { await api.resetStatus(); } catch(e) {}
  };

  // GeoGate — block until geo allowed
  if (!geoReady) return (
    <>
      <style>{css}</style>
      <GeoGate onAllowed={g => { if(g) localStorage.setItem('vfs_geo', g); setGeo(g); setGeoReady(true); }}/>
    </>
  );

  if (screen !== 'main') return (<><style>{css}</style>
    {screen==='auth_email'&&<AuthPhone onResult={r=>{if(r.type==='FORBIDDEN')setScreen('forbidden');else if(r.type==='NEED_REGISTRATION'){setPhone(r.phone);setScreen('auth_set_pin');}else{setPhone(r.phone);setScreen('auth_verify_pin');}}}/>}
    {screen==='forbidden'&&<Forbidden/>}
    {screen==='auth_set_pin'&&<AuthSetPin phone={phone} geo={geo} onSuccess={goMain}/>}
    {screen==='auth_verify_pin'&&<AuthVerifyPin phone={phone} geo={geo} onSuccess={goMain} onForgot={async()=>{await api.forgotPin(phone);setScreen('auth_set_pin');}}/>}
    {screen==='ask_notif'&&<NotifGate onDone={()=>{ localStorage.setItem('vfs_notif_asked','1'); setNotifAsked(true); setScreen('main'); setTimeout(()=>window._initFCM?.(),500); }}/>}
  </>);

  // Loading — white bg to avoid blue flash
  if (!status) return (
    <>
      <style>{css}</style>
      <div style={{minHeight:'100vh',display:'flex',alignItems:'center',justifyContent:'center',background:'#F0F2F5'}}>
        <div style={{width:36,height:36,border:'3px solid #DDE3EA',borderTopColor:'#06345B',borderRadius:'50%',animation:'spin 1s linear infinite'}}/>
      </div>
    </>
  );

  const s = status.app_status;
  if (s==='error' && status.error_message === 'update_app') return (<><style>{css}</style>
    <div style={{minHeight:'100vh',display:'flex',flexDirection:'column',background:'linear-gradient(150deg,#031E3A,#06345B)'}}>
      <div className="app-bg"/>
      <StatusBar/>
      <div style={{flex:1,display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',padding:'40px 28px',gap:20,textAlign:'center'}}>
        <div style={{width:72,height:72,background:'rgba(255,255,255,.1)',border:'2px solid rgba(255,255,255,.2)',borderRadius:20,display:'flex',alignItems:'center',justifyContent:'center',fontSize:36}}>🔄</div>
        <div style={{fontSize:22,fontWeight:800,color:'white',letterSpacing:'-.4px'}}>Обновите приложение</div>
        <div style={{fontSize:14,color:'rgba(255,255,255,.55)',maxWidth:280,lineHeight:1.6}}>Пожалуйста обновите приложение до последней версии или свяжитесь с вашим работодателем</div>
        <button onClick={()=>window.location.reload()} style={{background:'linear-gradient(135deg,#0052CC,#0A4A80)',border:'none',borderRadius:14,color:'white',padding:'15px 32px',fontSize:15,fontWeight:700,cursor:'pointer',fontFamily:'inherit',boxShadow:'0 4px 20px rgba(0,82,204,.4)',marginTop:8}}>Обновить сейчас</button>
      </div>
    </div>
  </>);
  if (s==='pending') return (<><style>{css}</style><PendingScreen status={status} onCancel={hCancel}/></>);
  if (s==='success') return (<><style>{css}</style><SuccessScreen status={status}/></>);
  if (s==='error') return (<><style>{css}</style><ErrorScreen status={status} onUpload={()=>setStatus(s=>({...s,app_status:'pending',error_message:null}))} onReset={hReset}/></>);

  return (<><style>{css}</style>
    <div className="app-bg"/>
    {tab==='booking'&&<BookingPage manager={status.manager} onSubmitted={()=>setStatus(s=>({...s,app_status:'pending'}))}/>}
    {tab==='support'&&<><SupportPage/><BottomNav active={tab} onChange={setTab}/></>}
    {tab==='profile'&&(<div style={{display:'flex',flexDirection:'column',minHeight:'100vh'}}><StatusBar/><div className="page"><ProfilePage status={status}/></div></div>)}
    <BottomNav active={tab} onChange={setTab}/>
  </>);
}
